const { Icons } = require('./RiskIndicatorIcons/index.js');
const { AD_ENTITY_TYPES, INSIGHTS_FIELD_TYPES, INSIGHTS_REASON_FIELDS_REGEX } = require('./consts.js');

/*
    This file documents both entity Insights and RI insights that are added to logs.
    Each Entity Insight is will follow the schema:

    {
        init_timestamp: float
        last_update_timestamp: float
        insight_status: 'risky' | 'hidden' | 'disabled'
    }

    It could also have extra fields that change for each insight. For example:
    OLD_OS will have an extra field called 'operating_system' that hold a string that represents the type of operating system that is running on the server with the RI
    we will use the extraEntityDetails to determine how to extract the extra data from the Entity Insight

    The logs are more complicated but we will use the extraLogDetails to extract the extra data from them
*/

/*
    Keys - Should all be in UPPER_CASE_SNAKE_CASE
    Values - the rules/ insights /risk indicators as they are sent from Jorah
*/
const RISK_RULES = {
    AS_REP_ROASTING: 'AS_REP_ROASTING',
    AZURE_HIGH_RISK: 'AZURE_HIGH_RISK',
    AZURE_LOW_RISK: 'AZURE_LOW_RISK',
    AZURE_MEDIUM_RISK: 'AZURE_MEDIUM_RISK',
    USER_BRUTEFORCE_RULE: 'bruteforce_user_rule',
    NTLM_V1: 'NTLM_V1',
    NTLM_V2: 'NTLM_V2',
    PASSWORD_NEVER_EXPIRES: 'password_never_expires_rule',
    USER_BRUTEFORCE: 'USER_BRUTEFORCE',
    KERBEROASTING: 'KERBEROASTING',
    ABNORMAL_AUTHENTICATION: 'ABNORMAL_AUTHENTICATION',
    ABNORMAL_MFA_USER_RULE: 'abnormal_mfa_user_rule',
    MFA_BOMBING: 'MFA_BOMBING',
    ADMIN_WITH_SPN: 'admin_with_SPN_rule',
    ANONYMOUS_IP: 'ANONYMOUS_IP',
    ABNORMAL_GEOLOCATION: 'ABNORMAL_GEOLOCATION',
    EXCESSIVE_ACTIVITY: 'EXCESSIVE_ACTIVITY',
    EXCESSIVE_ACTIVITY_RULE: 'excessive_activity_rule',
    NEW_SERVER: 'NEW_SERVER',
    INVESTIGATIONS_THREAT_INTELLIGENCE: 'INVESTIGATIONS_THREAT_INTELLIGENCE',
    BAD_CHECKSUM: 'BAD_CHECKSUM',
    BAD_CHECKSUM_USER: 'BAD_CHECKSUM_USER',
    CERTIFRIED: 'CERTIFRIED',
    CERTIFICATE_AUTH: 'CERTIFICATE_AUTH',
    PASSWORD_AUTH: 'PASSWORD_AUTH',
    CREDENTIAL_SCANNING: 'CREDENTIAL_SCANNING',
    CREDENTIAL_SCANNING_RULE: 'credential_scanning_rule',
    ABNORMAL_DAY: 'ABNORMAL_DAY',
    ABNORMAL_DAY_USER: 'abnormal_day_user_rule',
    ABNORMAL_DESTINATION: 'ABNORMAL_DESTINATION',
    DOMAIN_ADMIN: 'domain_admin_rule',
    ABNORMAL_HOUR: 'ABNORMAL_HOUR',
    ABNORMAL_HOUR_USER: 'abnormal_hour_user_rule',
    IRREGULAR_HOST: 'IRREGULAR_HOST',
    INTERACTIVE_LOGIN: 'INTERACTIVE_LOGIN',
    SERVICE_ACCOUNT_INTERACTIVE_LOGIN: 'service_account_interactive_login_rule',
    IMPOSSIBLE_TRAVEL: 'IMPOSSIBLE_TRAVEL',
    SPN_RECON: 'SPN_RECON',
    LEAKED_CREDENTIALS: 'LEAKED_CREDENTIALS',
    LOG4SHELL: 'LOG4SHELL',
    FORBIDDEN_CHARACTERS: 'FORBIDDEN_CHARACTERS',
    PAYLOAD_INJECTION: 'PAYLOAD_INJECTION',
    USER_PASSWORD_EXPOSED: 'USER_PASSWORD_EXPOSED',
    MALICIOUS_IP_RULE: 'malicious_ip_rule',
    MALICIOUS_IP: 'MALICIOUS_IP',
    NEW_USER_ACCOUNT: 'NEW_USER_ACCOUNT',
    NEW_CREATED_SERVER: 'NEW_CREATED_SERVER',
    NO_PAC: 'NO_PAC',
    NO_PAC_TGT_USAGE: 'NO_PAC_TGT_USAGE',
    OLD_OS: 'old_OS_resource_rule',
    OLD_PASSWORD: 'password_last_set_user_rule',
    PRIVILEGED_USER: 'privileged_user_rule',
    UNPRIVILEGED_DNS_ADMIN: 'UNPRIVILEGED_DNS_ADMIN',
    HIGH_RISK_SOURCE: 'HIGH_RISK_SOURCE',
    CRITICAL_RISK_SOURCE: 'CRITICAL_RISK_SOURCE',
    HIGH_RISK_RESOURCE: 'HIGH_RISK_RESOURCE',
    CRITICAL_RISK_RESOURCE: 'CRITICAL_RISK_RESOURCE',
    SHARED_USER: 'shared_user_rule',
    SITE_ABNORMALITY: 'SITE_ABNORMALITY',
    SMB_ENUMERATION: 'SMB_ENUMERATION',
    STALE_USER: 'stale_user_rule',
    SERVER_BRUTEFORCE: 'SERVER_BRUTEFORCE',
    USE_OF_STALE_USER: 'use_of_stale_user',
    SHADOW_ADMIN: 'shadow_admin_rule',
    STALE_RESOURCE: 'stale_resource_rule',
    STALE_DOMAIN_CONTROLLER: 'STALE_DOMAIN_CONTROLLER',
    DOMAIN_CONTROLLER_OLD_PASSWORD: 'DOMAIN_CONTROLLER_OLD_PASSWORD',
    SHARED_DEVICE: 'shared_device_resource_rule',
    HOST_BRUTEFORCE: 'HOST_BRUTEFORCE',
    MIXED_SESSION_HOST: 'MIXED_SESSION_HOST',
    FAILED_AUTHS_BATCH: 'FAILED_AUTHS_BATCH',
    SERVICE_ACCOUNT: 'service_account_rule',
    HIGH_RISK_USER: 'HIGH_RISK_USER',
    CRITICAL_RISK_USER: 'CRITICAL_RISK_USER',
    LOCKED_USER: 'locked_account_user_rule',
    USER_WITH_SPN: 'user_with_SPN_rule',
    UNFAMILIAR_SIGN_IN: 'UNFAMILIAR_SIGN_IN',
    WEAKLY_ENCRYPTED_TICKET: 'WEAKLY_ENCRYPTED_TICKET',
    WEAKLY_ENCRYPTED_MESSAGE: 'WEAKLY_ENCRYPTED_MESSAGE',
    USER_WEAK_ENCRYPTION: 'USER_WEAK_ENCRYPTION',
    SERVER_WEAK_ENCRYPTION: 'SERVER_WEAK_ENCRYPTION',
    LDAP_OBSOLETE_TLS: 'LDAP_OBSOLETE_TLS',
    LDAP_WEAK_CIPHER: 'LDAP_WEAK_CIPHER',
    LDAP_ANONYMOUS_BIND: 'LDAP_ANONYMOUS_BIND',
    USE_OF_BUILT_IN_GUEST: 'USE_OF_BUILT_IN_GUEST',
    USE_OF_BUILT_IN_ADMIN: 'USE_OF_BUILT_IN_ADMIN',
    HISTORIC_ENCRYPTION: 'HISTORIC_ENCRYPTION',
    PRIVILEGED_COMPUTER: 'PRIVILEGED_COMPUTER',
    RPC_SEALING_NOT_ENCRYPTED: 'RPC_SEALING_NOT_ENCRYPTED',
    PRINT_NIGHTMARE_FIX: 'PRINT_NIGHTMARE_FIX',
    S4U2SELF: 'S4U2SELF',
    S4U2PROXY: 'S4U2PROXY',
    RODC_PRIVILEGED_CREDENTIALS: 'RODC_PRIVILEGED_CREDENTIALS',
    SERVER_BRUTEFORCE_RULE: 'bruteforce_resource_rule',
    ACCOUNT_ENUMERATION: 'ACCOUNT_ENUMERATION',
    AZURE_SHADOW_ADMIN: 'AZURE_SHADOW_ADMIN',
    AZURE_PRIVILEGED_GUEST: 'AZURE_PRIVILEGED_GUEST',
    ENTRA_ID_ADMIN: 'ENTRA_ID_ADMIN',
    ENTRA_ID_SYNCED_ADMIN: 'ENTRA_ID_SYNCED_ADMIN',
    RECURRENT_FAILURES: 'RECURRENT_FAILURES',
    UNCONSTRAINED_DELEGATION_RESOURCE: 'unconstrained_delegation_resource_rule',
    UNCONSTRAINED_DELEGATION_USER: 'unconstrained_delegation_user_rule',
    WEAK_ENCRYPTION: 'WEAK_ENCRYPTION',
    DOMAIN_CONTROLLER: 'DOMAIN_CONTROLLER',
    DOMAIN_CONTROLLER_SHADOW: 'DOMAIN_CONTROLLER_SHADOW',
    DC_OWNED_BY_NON_ADMIN: 'DC_OWNED_BY_NON_ADMIN',
    NON_ADMIN_DC_OWNER: 'NON_ADMIN_DC_OWNER',
    USER_RETROSPECTIVE: 'user_retrospective_rule',
    TICKET_FROM_NEW_IP: 'TICKET_FROM_NEW_IP',
    LOCKED_AFTER_PASSWORD_CHANGE: 'locked_after_password_change',
    PASSWORD_NOT_REQUIRED: 'user_password_not_required',
    USE_DES_KEY_ONLY: 'user_encryption_use_des_key_only',
    DONT_REQUIRE_PREAUTH: 'user_dont_require_pre_auth',
    PARTIAL_SECRETS_ACCOUNT: 'user_partial_secret_account',
    ENCRYPTED_TEXT_PASSWORD_ALLOWED: 'user_encrypted_text_password_allowed',
    REPETITIVE_BEHAVIOR: 'REPETITIVE_BEHAVIOR',
    ENABLED_BUILT_IN_GUEST: 'ENABLED_BUILT_IN_GUEST',
    RENAMED_BUILT_IN_GUEST: 'RENAMED_BUILT_IN_GUEST',
    ENABLED_BUILT_IN_ADMIN: 'ENABLED_BUILT_IN_ADMIN',
    RENAMED_BUILT_IN_ADMIN: 'RENAMED_BUILT_IN_ADMIN',
    SAPPHIRE_TICKET: 'SAPPHIRE_TICKET',
    UNMATCHED_TGT: 'UNMATCHED_TGT',
    UNPRIVILEGED_CERTIFICATE_TEMPLATE_MODIFIER: 'UNPRIVILEGED_CERTIFICATE_TEMPLATE_MODIFIER',
    KRBTGT_ACCOUNT: 'KRBTGT_ACCOUNT',
    REMOTE_SERVICE_INSTALLATION: 'REMOTE_SERVICE_INSTALLATION',
    PRIVILEGED_USER_NOT_IN_PROTECTED_USERS_GROUP: 'PRIVILEGED_USER_NOT_IN_PROTECTED_USERS_GROUP',
    PRIVILEGED_GROUP_MEMBER_ADDITION: 'PRIVILEGED_GROUP_MEMBER_ADDITION',
    SECURITY_LOG_CLEARED: 'SECURITY_LOG_CLEARED'
};

const INVENTORY_ITEMS = {
    AZURE_APPS: 'azure_apps',
    OKTA_APPS: 'okta_apps'
};

/**
 * @typedef RiskCategory
 * @property {string} key - The ID of the risk category.
 * @property {string} title - The title of the risk category.
 */

const RISK_CATEGORIES = {
    UNSECURE_CONFIGURATION: {
        key: 'UNSECURE_CONFIGURATION',
        title: 'Unsecure Configuration'
    },
    CREDENTIAL_ACCESS: {
        key: 'CREDENTIAL_ACCESS',
        title: 'Credential Access'
    },
    PRIVILEGE_ESCALATION: {
        key: 'PRIVILEGE_ESCALATION',
        title: 'Privilege Escalation'
    },
    CUSTOM_INSIGHTS: {
        key: 'CUSTOM_INSIGHTS',
        title: 'Custom Exposures'
    }
};

const RISK_SEVERITY = {
    CRITICAL: 4,
    HIGH: 3,
    MEDIUM: 2,
    LOW: 1,
    SECURE: 0
};

/**
 * @typedef {Object} Icons
 * @property {string} dark - The dark mode icon.
 * @property {string} light - The light mode icon.
 */

/**
 * @typedef {Object} ExtraEntityDetail
 * @property {string} moreDetailsKey - The key for the extra detail.
 * @property {string} moreDetailsHeader - The header for the extra detail.
 * @property {string} [moreDetailsFormat] - Omit for a string value, use 'date' or 'list' when you need to
 */

/**
 * @typedef {Object} ExtraLogDetail
 * @property {string} extraDetailsType - The type of extra detail.
 * @property {string} extraDetailsField - The field for the extra detail.
 */

/**
 * @typedef {Object} RiskIndicator
 * @property {string} [authTitle] - LOGS page (multiple UI places), POLICIES page (if enabled 'policyRule' not null, will be presented as an option in the risk-based-policy-dropdown), INVESTIGATION page (entity activities details)
 * @property {string} [entityTitle] - INVESTIGATION page under entity's Risk Indicators, timeline, collapsed row, etc...
 * @property {string} [insightsTitle] - Insight push notification, INSIGHTS page, SYSTEM-SETTINGS page -> notifications/alert, pptx risk indicators report
 * @property {Array<string>} riskRules - The connection to the Jorah and rule engine
 * @property {number} [adEntityType] - Is this Risk indicator is related to a user/server entity, otherwise leave blank
 * @property {string} [description] - A description of the risk indicator.
 * @property {string} [mitigation] - Mitigation strategies for the risk.
 * @property {string} [riskReportMitigation] - should only be present if mitigation on report pptx is different from regular mitigation shown in UI
 * @property {Object} [reference] - Reference links and descriptions.
 * @property {boolean} [sendToSplunk] - Will send all rules of this risk indicator to splunk
 * @property {Icons} [icons] - In case only one attribute is defined (light / dark) it will be used for both cases, no need to define the same icon for both light/dark
 * @property {Array<ExtraEntityDetail>} [extraEntityDetails] - Additional details about the entity.
 * @property {ExtraLogDetail} [extraLogDetails] - Additional details for logging.
 * @property {boolean} [shownForKRBTGT] - If true, show this RI (if found) on krbTGT entity
 * @property {string} [policyRule] - If exists RI will be shown in the policies dropdown, also a single rule to send to the wall (rule engine) to check the policy on
 * @property {boolean} [usedByFilters] - True if this RI should be shown in the LOGS page filter and allow to filter by it
 * @property {string} [syslogValue] - The syslog value associated with this risk indicator.
 * @property {string} [customInsightRule] - Matching custom insight rule. If exists, customization will be enabled (exclusion only)
 * @property {boolean} [enableInclusionOnCustomInsight] - True if it should allow customization (exclusion and inclusion), will work only if 'customInsightRule' exists
 * @property{Object} reason - The explainability definition of the risk indicator.
 * @property {RiskCategory} [category] - The category of the risk indicator.
 * @property {number} [severity] - The severity of the risk indicator.
 * @property {boolean} [isPostureInsight] - True if this RI is an exposure.
 */

/**
 * holds the risk indicators that are used in the application
 * @type {{[riskName: string]: RiskIndicator}}
 */

const INDICATOR_TYPES = {
    ABNORMALITIES: 'Abnormalities',
    INCIDENT: 'Incident',
    EXPOSURE: 'Exposure',
    EXTERNAL: 'External'
};

const RISK_INDICATORS = {
    ABNORMAL_AUTHENTICATION: {
        authTitle: 'Abnormal Authentication',
        entityTitle: 'Abnormal Authentication',
        insightsTitle: 'Abnormal Authentication',
        description:
            'The request is unusual for the user or service making the request, based on their authentication history.',
        reference: {
            desc: 'MITRE Attack T1078.002 - Valid Accounts: Domain Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/002/'
        },
        icons: {
            dark: Icons.highRiskAuthsIcon,
            light: Icons.highRiskAuthsIcon
        },
        riskRules: [RISK_RULES.ABNORMAL_AUTHENTICATION],
        policyRule: RISK_RULES.ABNORMAL_AUTHENTICATION,
        usedByFilters: true,
        syslogValue: 'Abnormal_authentication_detected',
        type: INDICATOR_TYPES.ABNORMALITIES
    },
    ABNORMAL_DESTINATION: {
        authTitle: 'Abnormal Destination For User',
        entityTitle: 'Abnormal Destination For User',
        insightsTitle: 'Abnormal Destination For User',
        icons: {
            dark: Icons.highRiskAuthsIcon,
            light: Icons.highRiskAuthsIcon
        },
        riskRules: [RISK_RULES.ABNORMAL_DESTINATION],
        policyRule: RISK_RULES.ABNORMAL_DESTINATION,
        usedByFilters: true,
        syslogValue: 'Abnormal_destination_for_user',
        type: INDICATOR_TYPES.ABNORMALITIES,
        customInsightRule: RISK_RULES.ABNORMAL_DESTINATION
    },
    ABNORMAL_GEOLOCATION: {
        authTitle: 'Abnormal Geolocation',
        entityTitle: 'Abnormal Geolocation',
        insightsTitle: 'Abnormal Geolocation',
        icons: {
            dark: Icons.AbnormalGeolocationIcon,
            light: Icons.AbnormalGeolocationIcon
        },
        riskRules: [RISK_RULES.ABNORMAL_GEOLOCATION],
        policyRule: RISK_RULES.ABNORMAL_GEOLOCATION,
        usedByFilters: true,
        syslogValue: 'Abnormal_Geolocation',
        type: INDICATOR_TYPES.ABNORMALITIES,
        customInsightRule: RISK_RULES.ABNORMAL_GEOLOCATION
    },
    ABNORMAL_MFA: {
        icons: {
            dark: Icons.deniedMFAIcon,
            light: Icons.deniedMFAIcon
        },
        authTitle: 'Abnormal MFA Activity',
        entityTitle: 'Abnormal MFA Activity',
        insightsTitle: 'Abnormal MFA Activity',
        mitigation: `1. Clarify with the user the reason for the denied MFAs\n
        2. Have the use reset the password, if they didn't perform the authentications\n
        3. Ensure the new password is complex`,
        reference: {
            desc: 'MITRE Attack T1111 - Multi-Factor Authentication Interception',
            link: 'https://attack.mitre.org/techniques/T1111/'
        },
        policyRule: RISK_RULES.ABNORMAL_MFA_USER_RULE,
        riskRules: [RISK_RULES.ABNORMAL_MFA_USER_RULE],
        usedByFilters: true,
        syslogValue: 'Abnormal_MFA_activity',
        type: INDICATOR_TYPES.ABNORMALITIES,
        customInsightRule: RISK_RULES.ABNORMAL_MFA_USER_RULE
    },
    ADMIN_WITH_SPN: {
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.userWithSpnIcon,
            light: Icons.lightAdminWithSpnIcon
        },
        authTitle: 'Admin with SPN',
        entityTitle: 'Admin with SPN',
        insightsTitle: 'Admins with SPN',
        description:
            'These privileged users are susceptible to the “Kerberoasting” attack: An attacker can request these Kerberos tickets and can gain the payload encrypted by the user’s password hash. This payload can in turn be enumerated offline by the attacker, revealing the users password.',
        mitigation:
            'Remove SPN from Admin Users.\n' +
            'Prerequisites: Domain Admin or Enterprise Admin privileges in AD.\n' +
            'Process:\n' +
            '1. Press Win+R > type dsa.msc > press Enter, to open Active Directory Users and Computers (ADUC).\n' +
            '2. Enable Advanced Features by selecting View > Advanced Features.\n' +
            '3. Locate the User Account by navigating to the organizational unit (OU) where the user is located. Right-click the user and select Properties.\n' +
            '4. Remove the SPN by navigating to the Attribute Editor tab. Find the attribute servicePrincipalName, then select Edit > select the SPN you want to remove > Remove. Select Ok > Apply to save the changes.\n' +
            '5. Verify the SPN is removed by reopening the Attribute Editor, then confirm the servicePrincipalName does not include SPN.',
        reference: {
            desc: 'MITRE Attack T1558 - Steal or Forge Kerberos Tickets',
            link: 'https://attack.mitre.org/techniques/T1558/'
        },
        policyRule: RISK_RULES.ADMIN_WITH_SPN,
        riskRules: [RISK_RULES.ADMIN_WITH_SPN],
        customInsightRule: RISK_RULES.ADMIN_WITH_SPN,
        sendToSplunk: true,
        usedByFilters: true,
        syslogValue: 'Admin_with_SPN',
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.CRITICAL,
        type: INDICATOR_TYPES.EXPOSURE
    },
    ANONYMOUS_IP: {
        icons: {
            dark: Icons.AnonymousIpIcon,
            light: Icons.AnonymousIpIcon
        },
        authTitle: 'Anonymous IP',
        entityTitle: 'Anonymous IP',
        insightsTitle: 'Anonymous IP',
        reference: {
            desc: 'MITRE Attack T1604 - Proxy Through Victim',
            link: 'https://attack.mitre.org/techniques/T1604/'
        },
        policyRule: RISK_RULES.ANONYMOUS_IP,
        riskRules: [RISK_RULES.ANONYMOUS_IP],
        usedByFilters: true,
        syslogValue: 'Anonymous_IP',
        customInsightRule: RISK_RULES.ANONYMOUS_IP
    },
    AS_REP_ROASTING: {
        adEntityType: AD_ENTITY_TYPES.USER,
        authTitle: 'Suspected AS-REP Roasting',
        entityTitle: 'Suspected AS-REP Roasting',
        insightsTitle: 'Suspected AS-REP Roasting',
        description:
            'AS-REP Roasting is an attack against Kerberos for user accounts that are configured to not require preauthentication. If preauthentication is disabled, an attacker could request authentication data for any user and the DC would return an encrypted TGT that can be brute-forced offline. This indicator shows users for which suspected AS-REP roasting attacks were detected.',
        mitigation:
            "1. Uncheck the \"Do not require Kerberos preauthentication\" attribute (in the Account tab) for users in Active Directory Users and Computers.\n        2. Enforce complex and long passwords for user accounts, especially service accounts.\n        3. Reset the password If a suspected user doesn't have the attribute 'pre-authentication is required' set.",
        reference: {
            desc: 'MITRE Attack T1558.004 - Steal or Forge Kerberos Tickets: AS-REP Roasting',
            link: 'https://attack.mitre.org/techniques/T1558/004/'
        },
        icons: {
            dark: Icons.asRepRoastingIcon,
            light: Icons.lightAsRepRoastingIcon
        },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${host_guid|host_ip} without using pre-authentication.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                host_ip: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        policyRule: RISK_RULES.AS_REP_ROASTING,
        riskRules: [RISK_RULES.AS_REP_ROASTING],
        sendToSplunk: true,
        usedByFilters: true,
        type: INDICATOR_TYPES.INCIDENT,
        syslogValue: 'Suspected_AS-REP_Roasting'
    },
    AZURE_RISKY_USER: {
        authTitle: 'Azure Risky User',
        entityTitle: 'Azure Risky User',
        insightsTitle: 'Azure Risky Users',
        reference: {
            desc: 'MITRE Attack T1078.004 - Valid Accounts: Cloud Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/004/'
        },
        type: INDICATOR_TYPES.EXTERNAL,
        icons: {
            dark: Icons.azureIcon,
            light: Icons.azureIcon
        },
        riskRules: [RISK_RULES.AZURE_LOW_RISK, RISK_RULES.AZURE_MEDIUM_RISK, RISK_RULES.AZURE_HIGH_RISK],
        usedByFilters: true,
        syslogValue: 'Azure_Risky_User'
    },
    BAD_CHECKSUM: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Bad Checksum in Kerberos',
        entityTitle: 'Bad Checksum in Kerberos',
        insightsTitle: 'Bad Checksums in Kerberos',
        description:
            'Authentication failures due to bad checksum found in a Kerberos packet. This indicates that the password used to encrypt the Kerberos service ticket is different from the target server. Commonly, this is due to identically named machine accounts in the target realm, and the client realm.',
        mitigation:
            'A bad checksum in a Kerberos packet is an indication that the Kerberos packet was modified or tampered with. The user who performed the authentication is suspected of being an attacker, and the host is suspected to be compromised.\n                        The host and user should both be disabled until the reason for the bad checksum is clear.',
        reference: {
            desc: 'MITRE Attack TA0006 - Credential Access',
            link: 'https://attack.mitre.org/tactics/TA0006/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.BadChecksumIcon,
            light: Icons.lightBadChecksumIcon
        },
        reason: {
            template:
                '${entityName} failed authentication at ${time}, from ${host_guid|host_ip} to ${server_guid}, due to a bad checksum found in a Kerberos packet.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                host_ip: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                server_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID
            }
        },
        policyRule: RISK_RULES.BAD_CHECKSUM,
        riskRules: [RISK_RULES.BAD_CHECKSUM, RISK_RULES.BAD_CHECKSUM_USER],
        usedByFilters: true,
        syslogValue: 'Bad_checksum_in_Kerberos'
    },
    EXCESSIVE_ACTIVITY: {
        type: INDICATOR_TYPES.ABNORMALITIES,
        authTitle: 'Authentication Spike',
        entityTitle: 'Authentication Spike',
        insightsTitle: 'Authentication Spike',
        mitigation:
            '1. Check whether the user or service account is performing more than the number of authentications that it usually performs.\n        2. If the user or service account is performing an unusual number of authentications, replace, or delete, the password.',
        reference: {
            desc: 'MITRE Attack T1110.003 - Brute Force: Password Spraying',
            link: 'https://attack.mitre.org/techniques/T1110/003/'
        },
        icons: {
            dark: Icons.authenticationSpikeIcon,
            light: Icons.authenticationSpikeIcon
        },
        riskRules: [RISK_RULES.EXCESSIVE_ACTIVITY, RISK_RULES.EXCESSIVE_ACTIVITY_RULE],
        policyRule: RISK_RULES.EXCESSIVE_ACTIVITY,
        usedByFilters: true,
        syslogValue: 'Authentication_spike',
        customInsightRule: RISK_RULES.EXCESSIVE_ACTIVITY_RULE
    },
    INVESTIGATIONS_THREAT_INTELLIGENCE: {
        type: INDICATOR_TYPES.EXTERNAL,
        authTitle: 'Azure Threat Intelligence',
        entityTitle: 'Azure Threat Intelligence',
        insightsTitle: 'Azure Threat Intelligence',
        icons: {
            dark: Icons.threatIntelligenceIcon,
            light: Icons.threatIntelligenceIcon
        },
        riskRules: [RISK_RULES.INVESTIGATIONS_THREAT_INTELLIGENCE],
        usedByFilters: true,
        syslogValue: 'Azure_threatIntelligence'
    },
    KERBEROASTING: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspected Kerberoasting',
        entityTitle: 'Suspected Kerberoasting',
        insightsTitle: 'Suspected Kerberoasting',
        description:
            'Kerberoasting is an attack that extracts password hashes for users with SPN from Active Directory for offline cracking. Kerberoasting exploits a combination of weak encryption and poor password hygiene.',
        mitigation:
            '1. Use Grouped Managed Service accounts instead of simple service accounts with persistent password.\n        2. Ensure strong password length and complexity, for service accounts, and ensure that these passwords periodically expire.\n        3. Limit permissions for all service accounts to the least required privileges necessary to function correctly.\n        4. Disable Weak encryption (in the weak encryption section).',
        reference: {
            desc: 'MITRE Attack T1558.003 - Steal or Forge Kerberos Tickets: Kerberoasting',
            link: 'https://attack.mitre.org/techniques/T1558/003/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.KerberoastingIcon,
            light: Icons.lightKerberoastingIcon
        },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${host_guid|host_ip} to user ${server_guid} and SPN ${spn} using a weak encryption ${encryption_type}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                host_ip: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                server_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                spn: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                encryption_type: INSIGHTS_FIELD_TYPES.RAW_FIELDS.ENCRYPTION_TYPE
            }
        },
        policyRule: RISK_RULES.KERBEROASTING,
        riskRules: [RISK_RULES.KERBEROASTING],
        sendToSplunk: true,
        usedByFilters: true,
        syslogValue: 'Suspected_Kerberoasting'
    },
    MFA_BOMBING: {
        type: INDICATOR_TYPES.INCIDENT,
        entityTitle: 'User Recently Denied Multiple MFA',
        authTitle: 'Suspected MFA Request Generation',
        insightsTitle: 'Suspected MFA Request Generation',
        description:
            'MFA Request Generation is a technique where the attacker can send multiple MFA notifications to the target mobile devices. This happens if they already gained the password but is still prohibited unless MFA allows the login. The victim might get confused about the unexpected notifications and accidentally tap approve, effectively granting access to the attacker.',
        mitigation:
            '1. Choking login attempts (temporarily locking an account after consecutive rejected attempts) by enabling an adaptive deny policy.\n        2. Providing additional context with MFA prompt messages, so that an attacker-initiated prompt is obviously bogus.\n        3. Binding the authentication to the session (for example, by asking the user to match a code displayed on the login screen).\n        4. Requiring that phone and endpoint device are in the same location (within the normal range of error bars of location services).',
        reference: {
            desc: 'MITRE Attack T1556 - Modify Authentication Process',
            link: 'https://attack.mitre.org/techniques/T1556/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.mfaBombingIcon,
            light: Icons.mfaBombingIcon
        },
        reason: {
            template:
                '${entityName} denied at least ${denied_mfa_request_count} MFA requests between ${start_time} and ${end_time}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                denied_mfa_request_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        policyRule: RISK_RULES.MFA_BOMBING,
        riskRules: [RISK_RULES.MFA_BOMBING],
        usedByFilters: true,
        syslogValue: 'Suspected_MFA_Request_Generation'
    },
    NEW_SERVER: {
        authTitle: 'Unusual Server Use',
        entityTitle: 'Unusual Server Use',
        insightsTitle: 'Unusual Server Uses',
        reference: {
            desc: 'MITRE Attack T1583/004 - Acquire Infrastructure: Server',
            link: 'https://attack.mitre.org/techniques/T1583/004/'
        },
        riskRules: [RISK_RULES.NEW_SERVER],
        usedByFilters: true,
        syslogValue: 'Unusual_server_use'
    },
    NTLM_V1: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'NTLMv1 Authentication',
        entityTitle: 'NTLMv1 Authentications',
        insightsTitle: 'NTLMv1 Authentications',
        description:
            "NTLMv1 is insecure because it uses weak encryption (DES) to encrypt the session key. This encryption type can be easily broken, and the user's password can be extracted.",
        mitigation:
            'Use NTLM v2 instead.\n' +
            'Process:\n' +
            '1. Enter gpedit.msc in a Run command window, to open a Group Policy window.\n' +
            '2. Navigate to Local Computer Policy > Computer Configuration > Windows Settings >Security Settings >Local Policies > Security Options.\n' +
            '3. Right-click on the policy Network Security: LAN Manager authentication level, and select Properties.\n' +
            '4. Select Send NTLMv2 response only/refuse LM & NTLM.\n' +
            '5. Select OK, and confirm the setting change.\n' +
            'After making these changes, monitor NTLM traffic and settings to see if further action is required.',
        riskReportMitigation: 'Use NTLM v2 instead.',
        reference: {
            desc: 'CVE-2018-1139',
            link: 'https://cve.mitre.org/cgi-bin/cvename.cgi?name=CVE-2018-1139'
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            dark: Icons.NtlmIcon,
            light: Icons.NtlmIcon
        },
        riskRules: [RISK_RULES.NTLM_V1],
        policyRule: RISK_RULES.NTLM_V1,
        customInsightRule: RISK_RULES.NTLM_V1,
        usedByFilters: true,
        syslogValue: 'NTLMv1_Authentication',
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.HIGH
    },
    NTLM_V2: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'NTLMv2 Authentication',
        entityTitle: 'NTLMv2 Authentications',
        insightsTitle: 'NTLMv2 Authentications',
        description:
            "NTLMv2, while stronger than v1, still lacks mutual authentication and remains vulnerable to relay attacks.",
        mitigation:
            'It is not recommended to use NTLM in any version of the protocol. If any machines must use this protocol, it is recommended implement compensating controls via Silverfort policies.',
        reference: {
            desc: 'Guide to eliminating NTLM completely from your environment',
            link: 'https://www.silverfort.com/blog/guide-to-eliminating-ntlm-completely-from-your-environment/'
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            dark: Icons.NtlmIcon,
            light: Icons.NtlmIcon
        },
        riskRules: [RISK_RULES.NTLM_V2],
        policyRule: RISK_RULES.NTLM_V2,
        customInsightRule: RISK_RULES.NTLM_V2,
        usedByFilters: true,
        syslogValue: 'NTLMv2_Authentication',
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.MEDIUM
    },
    PASSWORD_NEVER_EXPIRES: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Password Never Expires',
        entityTitle: 'Password Never Expires',
        insightsTitle: 'Passwords Never Expires',
        description:
            'Passwords that are set to “never expire”, not including service accounts. These passwords might be compromised and could be considered an attack vector. ',
        mitigation:
            'Create a policy to override this setting.\n' +
            'Prerequisites:\n' +
            '- Domain Admin or Account Operators permissions\n' +
            '- Confirm the target user account is not controlled by a fine-grained password.\n' +
            ' Process:\n' +
            '1. Press Win+R > type dsa.msc > press Enter, to open Active Directory Users and Computers (ADUC).\n' +
            '2. Enable Advanced Features by selecting View > Advanced Features.\n' +
            '3. Locate the User Account by navigating to the organizational unit (OU) where the user is located. Right-click the user and select Properties.\n' +
            '4. Select the Account tab and uncheck Password never expires , to disable this setting. Select Apply > OK to save the changes.\n' +
            '5. Verify the setting is disabled by reopening the user properties and confirming Password never expires is unchecked.',
        reference: {
            desc: 'MITRE Attack T1098 - Account Manipulation',
            link: 'https://attack.mitre.org/techniques/T1098'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.passwordNeverExpiresIcon,
            light: Icons.lightPasswordNeverExpiresIcon
        },
        policyRule: RISK_RULES.PASSWORD_NEVER_EXPIRES,
        riskRules: [RISK_RULES.PASSWORD_NEVER_EXPIRES],
        customInsightRule: RISK_RULES.PASSWORD_NEVER_EXPIRES,
        sendToSplunk: true,
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    USER_BRUTEFORCE: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspected User Brute Force',
        entityTitle: 'Suspected User Brute Force',
        insightsTitle: 'Suspected User Brute Force',
        description:
            'Adversaries use brute force techniques to gain access to accounts when passwords are unknown or when password hashes are obtained. Without knowledge of the password for an account or set of accounts, an adversary may systematically guess the password using a repetitive or iterative mechanism. Attackers can use brute force methods by interacting with a service that will check the validity of those credentials. It can also work offline against previously acquired credential data, such as password hashes.',
        mitigation:
            'Check whether the user is running a script using the wrong credentials, and, If so, reset the account password and investigate how a potential attacker could have retrieved the password.',
        icons: {
            dark: Icons.bruteForceIcon,
            light: Icons.bruteForceIcon
        },
        reason: {
            template:
                '${entityName} failed at least ${failed_authentications_count} authentications, between ${start_time} and ${end_time}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                failed_authentications_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        policyRule: RISK_RULES.USER_BRUTEFORCE,
        riskRules: [RISK_RULES.USER_BRUTEFORCE, RISK_RULES.USER_BRUTEFORCE_RULE],
        usedByFilters: true,
        syslogValue: 'Suspected_brute_force_attack_on_the_user',
        adEntityType: AD_ENTITY_TYPES.USER
    },
    CERTIFRIED: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspected Certifried Exploit',
        entityTitle: 'Suspected Certifried Exploit',
        insightsTitle: 'Suspected Certifried Exploits',
        description:
            'Certifried vulnerability (active directory domain privilege escalation - CVE-2022-26923) is an elevation of privilege that can occur when the Kerberos Distribution Center (KDC) is servicing a certificate-based authentication request. An authenticated user could manipulate attributes on computer accounts they own or manage, and acquire a certificate from Active Directory Certificate Services that would allow elevation of privilege to the system.',
        mitigation:
            'Update all servers that run Active Directory Certificate Services and Windows domain controllers that \n        service certificate-based authentication with the May 10, 2022 update (Microsoft patch), and enable Full Enforcement mode on all domain controllers. \n        In this mode, if a certificate fails the strong (secure) mapping criteria authentication will be denied.',
        reference: {
            desc: 'CVE-2022-26923 Active Directory Domain Services Elevation of Privilege Vulnerability',
            link: 'https://cve.mitre.org/cgi-bin/cvename.cgi?name=CVE-2022-26923'
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            dark: Icons.CertifriedIcon,
            light: Icons.lightCertifriedIcon
        },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${host_guid} to ${server_guid} using certificate-based authentication, which could indicate a Certifried exploit attempt.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                server_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID
            }
        },
        policyRule: RISK_RULES.CERTIFRIED,
        riskRules: [RISK_RULES.CERTIFRIED],
        usedByFilters: true,
        syslogValue: 'Suspected_Certifried_exploit'
    },
    CERTIFICATE_AUTH: {
        authTitle: 'Certificate-based',
        riskRules: [RISK_RULES.CERTIFICATE_AUTH],
        policyRule: RISK_RULES.CERTIFICATE_AUTH,
        icons: {
            light: Icons.lightCertificateAuthIcon,
            dark: Icons.darkCertificateAuthIcon
        },
        usedByFilters: true,
        syslogValue: 'Certificate-based'
    },
    PASSWORD_AUTH: {
        authTitle: 'Password-based',
        riskRules: [RISK_RULES.PASSWORD_AUTH],
        policyRule: RISK_RULES.PASSWORD_AUTH,
        icons: {
            light: Icons.passwordAuthIcon,
            dark: Icons.passwordAuthIcon
        },
        usedByFilters: true,
        syslogValue: 'Password-based'
    },
    CREDENTIAL_SCANNING: {
        authTitle: 'Credential Scanning',
        entityTitle: 'Credential Scanning',
        insightsTitle: 'Credential Scanning',
        description:
            'Brute force attack logins have been attempted on many different accounts on a network, with a single password, to avoid potential account lockouts that could occur when attempting a brute force attack on a single account with many passwords.',
        mitigation:
            '1. Set an account lockout policy for users, that is applied after a certain number of failed login attempts. This prevents passwords from being guessed. Choose the threshold for failed logins carefully, as a policy that is too strict may create a denial of service condition, and render environments unusable for accounts that are subject to brute force attacks.\n        2. Create a password policy that enforces strong passwords',
        reference: {
            desc: 'MITRE Attack T1110.003 - Brute Force: Password Spraying',
            link: 'https://attack.mitre.org/techniques/T1110/003/'
        },
        icons: {
            dark: Icons.CredentialScanningIcon,
            light: Icons.CredentialScanningIcon
        },
        reason: {
            template:
                '${entityName} received at least ${failed_authentications_count} failed authentications, between ${start_time} and ${end_time}, by at least ${users_count}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                failed_authentications_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                users_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER
            }
        },
        policyRule: RISK_RULES.CREDENTIAL_SCANNING_RULE,
        riskRules: [RISK_RULES.CREDENTIAL_SCANNING, RISK_RULES.CREDENTIAL_SCANNING_RULE],
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Credential_scanning',
        adEntityType: AD_ENTITY_TYPES.SERVER,
        type: INDICATOR_TYPES.INCIDENT
    },
    ABNORMAL_DAY: {
        type: INDICATOR_TYPES.ABNORMALITIES,
        authTitle: 'Daily Abnormal Authentication Activity',
        entityTitle: 'Daily Abnormal Authentication Activity',
        insightsTitle: 'Daily Abnormal Authentication Activity',
        riskRules: [RISK_RULES.ABNORMAL_DAY, RISK_RULES.ABNORMAL_DAY_USER],
        policyRule: RISK_RULES.ABNORMAL_DAY,
        icons: {
            light: Icons.highRiskAuthsIcon,
            dark: Icons.highRiskAuthsIcon
        },
        usedByFilters: true,
        syslogValue: 'Daily_abnormal_authentication_activity',
        customInsightRule: RISK_RULES.ABNORMAL_DAY_USER
    },
    DOMAIN_ADMIN: {
        authTitle: 'Domain Admin',
        entityTitle: 'Domain Admin',
        insightsTitle: 'Domain Admins',
        description:
            'List of Domain Admins in the directories protected by Silverfort. To minimize the number of attack vectors, it is recommended to validate that only real Domain Admins are included in the Domain Admin group.',
        riskRules: [RISK_RULES.DOMAIN_ADMIN],
        policyRule: RISK_RULES.DOMAIN_ADMIN,
        customInsightRule: RISK_RULES.DOMAIN_ADMIN,
        icons: {
            light: Icons.lightDomainAdminIcon,
            dark: Icons.domainAdminIcon
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        usedByFilters: true,
        syslogValue: 'Domain_admin',
        isPostureInsightForNotification: true
    },
    ABNORMAL_HOUR: {
        type: INDICATOR_TYPES.ABNORMALITIES,
        authTitle: 'Hourly Abnormal Authentication Activity',
        entityTitle: 'Hourly Abnormal Authentication Activity',
        insightsTitle: 'Hourly Abnormal Authentication Activities',
        riskRules: [RISK_RULES.ABNORMAL_HOUR, RISK_RULES.ABNORMAL_HOUR_USER],
        policyRule: RISK_RULES.ABNORMAL_HOUR,
        icons: {
            light: Icons.highRiskAuthsIcon,
            dark: Icons.highRiskAuthsIcon
        },
        usedByFilters: true,
        syslogValue: 'Hourly_abnormal_authentication_activity',
        customInsightRule: RISK_RULES.ABNORMAL_HOUR_USER
    },
    IRREGULAR_HOST: {
        authTitle: 'New Host for User',
        entityTitle: 'New Host For User',
        description:
            "Pass the Hash is a technique that allows an attacker to authenticate to a remote server or service by using the underlying hash of a user's password, instead of requiring the password itself. It replaces the need for stealing the plaintext password to gain access with stealing the hash by abusing the NTLM authentication protocol. Authentication of a user from an unusual host can be an indication for a potential Pass the Hash attack attempt.",
        mitigation:
            '1. Set a risk-based MFA policy to validate the identity of users who authenticate from a new host.\n' +
            '2. Monitor the activity of a user who authenticates from new host. This can be done using the user investigation and authentication logs.\n' +
            '3. Avoid permissions overlaps of users between systems to prevent possibility of lateral movement in case the hash has been stolen.',
        riskRules: [RISK_RULES.IRREGULAR_HOST],
        policyRule: RISK_RULES.IRREGULAR_HOST,
        reference: {
            desc: 'MITRE Attack T1550.002 - Pass the Hash',
            link: 'https://attack.mitre.org/techniques/T1550/002/'
        },
        icons: {
            light: Icons.passTheHashIcon,
            dark: Icons.passTheHashIcon
        },
        usedByFilters: true,
        syslogValue: 'New_host_for_user'
    },
    INTERACTIVE_LOGIN: {
        authTitle: 'Interactive Login',
        entityTitle: 'Interactive Login',
        insightsTitle: 'Interactive Login',
        reference: {
            desc: 'MITRE Attack T1556 - Modify Authentication Process',
            link: 'https://attack.mitre.org/techniques/T1556/'
        },
        riskRules: [RISK_RULES.INTERACTIVE_LOGIN],
        policyRule: RISK_RULES.INTERACTIVE_LOGIN,
        icons: { light: Icons.lightInteractiveLoginIcon, dark: Icons.interactiveLoginIcon },
        usedByFilters: true,
        syslogValue: 'Interactive_login'
    },
    SERVICE_ACCOUNT_INTERACTIVE_LOGIN: {
        authTitle: 'Service Account Interactive Login',
        entityTitle: 'Service Account Interactive Login',
        insightsTitle: 'Service Account Interactive Login',
        reference: {
            desc: 'MITRE Attack T1556 - Modify Authentication Process',
            link: 'https://attack.mitre.org/techniques/T1556/'
        },
        riskRules: [RISK_RULES.SERVICE_ACCOUNT_INTERACTIVE_LOGIN],
        icons: { light: Icons.serviceAccountInteractiveLoginIcon, dark: Icons.serviceAccountInteractiveLoginIcon },
        usedByFilters: true,
        syslogValue: 'Service_account_interactive_login'
    },
    IMPOSSIBLE_TRAVEL: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Impossible Travel',
        entityTitle: 'Impossible Travel',
        insightsTitle: 'Impossible Travel',
        description: 'Authentication was requested from an impossible user geolocation.',
        mitigation:
            '1. Check whether the user changed their IP address\n        2. Change the password for the user, If the user did not make the authentication',
        reference: {
            desc: 'MITRE Attack T1078 - Valid Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/'
        },
        riskRules: [RISK_RULES.IMPOSSIBLE_TRAVEL],
        policyRule: RISK_RULES.IMPOSSIBLE_TRAVEL,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            light: Icons.impossibleTravelIcon,
            dark: Icons.impossibleTravelIcon
        },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${current_location} (${host_ip}), which is considered impossible, as the user has recently authenticated from ${other_locations}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                current_location: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                host_ip: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                other_locations: INSIGHTS_FIELD_TYPES.RAW_FIELDS.PRETTY_LIST
            }
        },
        usedByFilters: true,
        syslogValue: 'Impossible_Travel'
    },
    SPN_RECON: {
        authTitle: 'SPN Reconnaissance',
        entityTitle: 'SPN Reconnaissance',
        insightsTitle: 'SPN Reconnaissance',
        mitigation:
            'Disable the suspected sources of the SPN Reconnaissance and check if there is evidence of any attack or vulnerability (look for traces of GetUserSPN scripts in the system).',
        reference: {
            desc: 'MITRE Attack TA0043 - Reconnaissance',
            link: 'https://attack.mitre.org/tactics/TA0043/'
        },
        riskRules: [RISK_RULES.SPN_RECON],
        policyRule: RISK_RULES.SPN_RECON,
        icons: {
            light: Icons.reconnaissanceIcon,
            dark: Icons.reconnaissanceIcon
        },
        usedByFilters: true,
        syslogValue: 'SPN_Reconnaissance'
    },
    LEAKED_CREDENTIALS: {
        type: INDICATOR_TYPES.EXTERNAL,
        authTitle: 'Leaked Credentials',
        entityTitle: 'Leaked Credentials',
        insightsTitle: 'Leaked Credentials',
        description:
            'Microsoft Leaked Credentials Service (from Entra ID Protection) detected the account’s credentials in a stolen-credentials database.',
        mitigation:
            'Reset user password, reveiw latest activity, and adjust permissions.\n' +
            'Process:\n' +
            '1. Sign in to the Entra ID Admin Portal as a Security Administrator or Global Administrator. Locate the user in the Users menu and select Reset password.\n' +
            "2. Review the user's recent activity to locate any behavior anomalies or suspicious sign-ins.\n" +
            '3. If needed, disable the user and revoke their active sessions.\n' +
            'We also recommended:\n' +
            "- Reviewing Microsoft's guide to enable risk based conditional access policies- https://learn.microsoft.com/en-us/entra/id-protection/howto-identity-protection-configure-risk-policies.\n" +
            "- Communicating the password reset process to the user's owner and assist them with resetting their password.",
        reference: {
            desc: 'MITRE Attack TA0001 - Initial Access',
            link: 'https://attack.mitre.org/tactics/TA0001/'
        },
        riskRules: [RISK_RULES.LEAKED_CREDENTIALS],
        usedByFilters: true,
        syslogValue: 'Leaked_credentials',
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.HIGH
    },
    LOG4SHELL: {
        type: INDICATOR_TYPES.INCIDENT,
        entityTitle: 'Log4Shell Exploit Attempt',
        authTitle: 'Log4Shell Exploit Attempt',
        insightsTitle: 'Log4Shell Exploit Attempts',
        description:
            'Log4Shell is a remote code execution (RCE) vulnerability affecting the Apache Log4j library, versions 2.0-beta9 to 2.14.1. The vulnerability occurs when the Java Naming and Directory Interface (JNDI) attempts to resolve variables.',
        mitigation:
            'Immediately identify, mitigate, and update affected products that use Log4j to the latest patched version.\n        For environments using Java 8 or later, upgrade to Log4j version 2.17.0 (released December 17, 2021), or later.\n        For environments using Java 7, upgrade to Log4j version 2.12.3 (released December 21, 2021).\n        Since Java 7 has reached its End of Life, it is recommended that you update to Java 8 or newer regardless of this exploit.',
        reference: {
            desc: 'CVE-2021-44228 Active Directory Domain Services Elevation of Privilege Vulnerability',
            link: 'https://www.cisa.gov/uscert/ncas/alerts/aa21-356a'
        },
        riskRules: [RISK_RULES.LOG4SHELL],
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: { light: Icons.lightLog4ShellIcon, dark: Icons.Log4ShellIcon },
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Log4Shell_exploit_attempts'
    },
    FORBIDDEN_CHARACTERS: {
        authTitle: 'Forbidden Characters Were Used',
        entityTitle: 'Forbidden Characters Were Used',
        insightsTitle: 'Forbidden Characters Were Used',
        riskRules: [RISK_RULES.FORBIDDEN_CHARACTERS],
        usedByFilters: true,
        syslogValue: 'Forbidden_characters_were_used',
        customInsightRule: RISK_RULES.FORBIDDEN_CHARACTERS
    },
    PAYLOAD_INJECTION: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspected Payload Injection',
        entityTitle: 'Suspected Payload Injection',
        insightsTitle: 'Suspected Payload Injections',
        description:
            'Injection is a type of attack that transmits data (payload) to change the way commands are interpreted.\n         During an injection, an attacker will transmit malicious data to an interpreter.\n         Most data input sources can be used for injection, including user identifier which is a common path for an identity threat attack.',
        mitigation:
            'Investigate vulnerable entities in the system that might be a potential target for such an injection attack and look for suspicious activity.\n        Configure injection prevention rules in the network protection measures (including firewalls), and set risk-based policies (based on suspected payload injection indicator) to block malicious authentications.',
        reference: {
            desc: 'OWASP Top 10:2021 - A03 Injection',
            link: 'https://owasp.org/Top10/A03_2021-Injection/'
        },
        riskRules: [RISK_RULES.PAYLOAD_INJECTION],
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: { light: Icons.PayloadInjectionIcon, dark: Icons.PayloadInjectionIcon },
        reason: {
            template:
                '${entityName} received authentication at ${time} from ${host_guid|host_ip} by ${payload}, which contains suspicious characters.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                host_ip: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                payload: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        usedByFilters: true,
        syslogValue: 'Suspected_Payload_Injection'
    },
    USER_PASSWORD_EXPOSED: {
        type: INDICATOR_TYPES.EXPOSURE,
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.LOW,
        authTitle: "User's Password Was Exposed",
        entityTitle: "User's Password Was Exposed",
        insightsTitle: "User's Password Was Exposed",
        description:
            'A user mistakenly entered their password in the username field during authentication, making it visible in authentication logs. This exposure can lead to credential compromise if logs are accessed by unauthorized parties.',
        mitigation:
            'Educate users to carefully enter credentials in the correct fields. Implement logging policies that restrict access to authentication logs containing sensitive information. Consider resetting the exposed password if the exposure is recent.',
        riskRules: [RISK_RULES.USER_PASSWORD_EXPOSED],
        adEntityType: AD_ENTITY_TYPES.SERVER,
        usedByFilters: true,
        customInsightRule: RISK_RULES.USER_PASSWORD_EXPOSED,
        syslogValue: 'Users_Password_Was_Exposed',
        icons: { light: Icons.userPasswordExposedIcon, dark: Icons.userPasswordExposedIcon }
    },
    MALICIOUS_IP: {
        authTitle: 'Malicious IP',
        entityTitle: 'Malicious IP',
        insightsTitle: 'Malicious IP',
        description:
            'The source of the authentication comes from a malicious IP address, based on lists of known malicious addresses.',
        mitigation:
            'It is very likely the authenticated account has been breached. It is suggested to change the password and reset the target account.\n        1. Reset the account password, and if it is a service account, you can use the following Powershell command: "Reset-ADServiceAccountPassword -Identity"\n        2. Use firewall based rules to block unrecognized and malicious IP ranges.\n        3. Disable the account and target machine until the scope of the attack is clear.',
        riskRules: [RISK_RULES.MALICIOUS_IP, RISK_RULES.MALICIOUS_IP_RULE],
        policyRule: RISK_RULES.MALICIOUS_IP,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: { light: Icons.maliciousIPIcon, dark: Icons.maliciousIPIcon },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${host_ip}, which is in a malicious IP list${ip_classifications}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_ip: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                ip_classifications: INSIGHTS_FIELD_TYPES.RAW_FIELDS.CUSTOM_FUNCTION
            },
            customFormattingExtraData: {
                KNOWN_ABUSER: ' as a known abuser',
                KNOWN_ATTACKER: ' as a known attacker'
            },
            customFieldFormatter: (ip_classifications, extraData) => {
                if (Array.isArray(ip_classifications) && ip_classifications.length) {
                    const strList = ip_classifications.map((classification) => extraData[classification]);
                    if (strList.length) {
                        return strList.join(' and');
                    }
                }
                return '';
            }
        },
        usedByFilters: true,
        syslogValue: 'Malicious_IP',
        type: INDICATOR_TYPES.INCIDENT
    },
    NEW_USER_ACCOUNT: {
        authTitle: 'New Account',
        entityTitle: 'New Account',
        insightsTitle: 'New Accounts',
        reference: {
            desc: 'MITRE Attack T1136 - Create Account',
            link: 'https://attack.mitre.org/techniques/T1136/'
        },
        riskRules: [RISK_RULES.NEW_USER_ACCOUNT],
        policyRule: RISK_RULES.NEW_USER_ACCOUNT,
        icons: {
            light: Icons.newAccountIcon,
            dark: Icons.newAccountIcon
        },
        usedByFilters: true,
        syslogValue: 'New_Account'
    },
    NEW_CREATED_SERVER: {
        authTitle: 'New Server',
        entityTitle: 'New Server',
        insightsTitle: 'New Servers',
        riskRules: [RISK_RULES.NEW_CREATED_SERVER],
        policyRule: RISK_RULES.NEW_CREATED_SERVER,
        icons: { light: Icons.NewCreatedServerIcon, dark: Icons.NewCreatedServerIcon },
        usedByFilters: true,
        syslogValue: 'New_Server'
    },
    NO_PAC: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspected NoPac Breach',
        entityTitle: 'Suspected NoPac Breach',
        insightsTitle: 'Suspected NoPac Breaches',
        description:
            'NoPac vulnerabilities (CVE-2021-42278 and CVE-2021-42287) were detected. these vulnerabilities can enable an attacker to perform privilege escalation. The vulnerabilities use a machine account with a name similar to the DC in order to steal the ticket.',
        mitigation: 'Update Domain Controllers to the latest version of Windows, to mitigate the vulnerabilities.',
        reference: [
            {
                desc: 'CVE-2021-42278 Active Directory Domain Services Elevation of Privilege Vulnerability',
                link: 'https://cve.mitre.org/cgi-bin/cvename.cgi?name=CVE-2021-42278'
            },
            {
                desc: 'CVE-2021-42287 Active Directory Domain Services Elevation of Privilege Vulnerability',
                link: 'https://cve.mitre.org/cgi-bin/cvename.cgi?name=CVE-2021-42287'
            }
        ],
        adEntityType: AD_ENTITY_TYPES.SERVER,
        riskRules: [RISK_RULES.NO_PAC],
        policyRule: RISK_RULES.NO_PAC,
        icons: { light: Icons.RiskyResourceIcon, dark: Icons.RiskyResourceIcon },
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Suspected_"NoPac"_breach'
    },
    NO_PAC_TGT_USAGE: {
        authTitle: 'Suspected NoPac TGT Was Used',
        entityTitle: 'Suspected NoPac TGT Was Used',
        insightsTitle: 'Suspected NoPac TGT Was Used',
        riskRules: [RISK_RULES.NO_PAC_TGT_USAGE],
        policyRule: RISK_RULES.NO_PAC_TGT_USAGE,
        icons: { light: Icons.RiskyResourceIcon, dark: Icons.RiskyResourceIcon },
        usedByFilters: true,
        syslogValue: 'Suspected_NoPac_TGT_was_used'
    },
    OLD_OS: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Old Operating System',
        entityTitle: 'Old Operating System',
        insightsTitle: 'Old Operating Systems',
        description:
            'List of devices with Operating Systems that are no longer supported by Microsoft. Devices connected to your network that do not receive security updates could have vulnerabilities and be an attack surface.',
        mitigation:
            'Upgrade OS and add security measures.\n' +
            'Process:\n' +
            '1. Remove any decommissioned systems from the AD.\n' +
            '2. Upgrade to a supported OS.\n' +
            '2. Confirm group policy application via the Group Policy Management (gpmc.msc), then force policy updates on all systems.\n' +
            '3. Reconfigure security settings by enabling BitLocker (if applicable) > run Windows Defender/Security Baseline checks. Then apply the latest security patches.',
        reference: {
            desc: 'MITRE Attack T1082 - System Information Discovery',
            link: 'https://attack.mitre.org/techniques/T1082/'
        },
        riskRules: [RISK_RULES.OLD_OS],
        policyRule: RISK_RULES.OLD_OS,
        customInsightRule: RISK_RULES.OLD_OS,
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            light: Icons.lightOldOSIcon,
            dark: Icons.oldOSIcon
        },
        extraEntityDetails: [{ moreDetailsKey: 'operating_system', moreDetailsHeader: 'Operating System' }],
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Old_operating_system',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.HIGH
    },
    OLD_PASSWORD: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'User with Old Password',
        authTitle: 'User with Old Password',
        insightsTitle: 'Users with Old Password',
        description:
            'List of passwords that have not been changed in the last year. These passwords might be compromised and could be considered an attack vector.',
        mitigation:
            'Rest user password.\n' +
            'Prerequisites: \n' +
            '- Domain Admin, Account Operators, or Help Desk privileges to reset passwords.\n' +
            '- Confirm user must be allowed to have their password reset (check Group Policy and security settings).\n' +
            '\n' +
            'Process:\n' +
            '1. Press Win+R > type dsa.msc > press Enter, to open Active Directory Users and Computers (ADUC).\n' +
            '2. Locate the User Account by navigating to the organizational unit (OU) where the user is located. Right-click the user and select Reset Password.\n' +
            '3. Enter the new password, then select OK.\n' +
            '- (Optional) Uncheck User must change password at next logon if the user should keep the new password permanently.\n' +
            '- (Optional) Check ״Unlock the account״ if the account is locked out.\n' +
            '- Inform the user of their new password and advise them to change it after logging in.',
        reference: {
            desc: 'MITRE Attack T1098 - Account Manipulation',
            link: 'https://attack.mitre.org/techniques/T1098'
        },
        riskRules: [RISK_RULES.OLD_PASSWORD],
        policyRule: RISK_RULES.OLD_PASSWORD,
        customInsightRule: RISK_RULES.OLD_PASSWORD,
        adEntityType: AD_ENTITY_TYPES.USER,
        extraEntityDetails: [
            {
                moreDetailsKey: 'last_reset_time',
                moreDetailsHeader: 'Last reset time',
                moreDetailsFormat: 'date'
            }
        ],
        icons: {
            light: Icons.lightOldPasswordIcon,
            dark: Icons.oldPasswordIcon
        },
        usedByFilters: true,
        syslogValue: 'User_with_old_password',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    PRIVILEGED_USER: {
        entityTitle: 'Privileged User',
        authTitle: 'Privileged User',
        insightsTitle: 'Privileged Users',
        description:
            "Privileged accounts exist in many forms across an enterprise environment, and they pose significant security risks if not protected, managed and monitored. It's common to think about domain admins as the privileged users but there are additional privileged users like schema admins, enterprise admins and more.",
        reference: {
            desc: 'MITRE Attack TA004 - Privilege Escalation',
            link: 'https://attack.mitre.org/tactics/TA0004/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        riskRules: [RISK_RULES.PRIVILEGED_USER],
        policyRule: RISK_RULES.PRIVILEGED_USER,
        customInsightRule: RISK_RULES.PRIVILEGED_USER,
        icons: {
            light: Icons.lightPrivilegedUserIcon,
            dark: Icons.privilegedUserIcon
        },
        extraEntityDetails: [
            { moreDetailsKey: 'domain', moreDetailsHeader: 'Domain', dataSource: 'tower' },
            { moreDetailsKey: 'privileged_groups', moreDetailsHeader: 'Privileged Groups' }
        ],
        usedByFilters: true,
        syslogValue: 'Privileged_user',
        enableInclusionOnCustomInsight: true,
        isPostureInsightForNotification: true
    },
    UNPRIVILEGED_DNS_ADMIN: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Unprivileged DNS Admins',
        entityTitle: 'Unprivileged DNS Admins',
        insightsTitle: 'Unprivileged DNS Admins',
        description:
            'Members of the DnsAdmins group can load arbitrary Dynamic Link Libraries (DLLs) into the DNS service running on a Domain Controller (DC). This can be used to execute code as NT AUTHORITY\\SYSTEM, which is the highest level of privilege on a Windows system. A non-privileged user in the DnsAdmins group can effectively become a Domain Admin by exploiting this DLL loading capability.',
        mitigation:
            'Open Active Directory Users and Computers (ADUC) → Navigate to: Built-in > DNS Admins\n' +
            'Remove unauthorized or unprivileged users from the DNS Admins group.\n' +
            'Delegate DNS Permissions: For fine-grained control:\n' +
            '   - Open DNS Manager (dnsmgmt.msc).\n' +
            '   - Right-click the desired DNS zone → Properties.\n' +
            '   - Go to the Security tab → Advanced → Add.\n' +
            '   - Select a user/group and assign specific permissions (e.g., Read, Create Child, Write).\n' +
            'Restrict DNS Management Console Access:\n' +
            '   Use Group Policy to limit who can run dnsmgmt.msc:\n' +
            '      - Open Group Policy Management (gpmc.msc).\n' +
            '      - Navigate to: User Configuration > Administrative Templates > System\n' +
            '      - Enable Don’t run specified Windows applications.\n' +
            '      - Add dnsmgmt.msc.',
        adEntityType: AD_ENTITY_TYPES.USER,
        riskRules: [RISK_RULES.UNPRIVILEGED_DNS_ADMIN],
        policyRule: RISK_RULES.UNPRIVILEGED_DNS_ADMIN,
        customInsightRule: RISK_RULES.UNPRIVILEGED_DNS_ADMIN,
        usedByFilters: true,
        syslogValue: 'Unprivileged_User_as_DNS_Admin',
        isPostureInsight: true,
        icons: {
            light: Icons.unprivilegedDnsAdminLight,
            dark: Icons.unprivilegedDnsAdminDark
        },
        category: RISK_CATEGORIES.PRIVILEGE_ESCALATION,
        severity: RISK_SEVERITY.CRITICAL
    },
    HIGH_RISK_SOURCE: {
        authTitle: 'Source is at High Risk',
        entityTitle: 'Source is at High Risk',
        insightsTitle: 'Sources are at High Risk',
        riskRules: [RISK_RULES.HIGH_RISK_SOURCE],
        policyRule: RISK_RULES.HIGH_RISK_SOURCE,
        icons: {
            light: Icons.highRiskResourceIcon,
            dark: Icons.highRiskResourceIcon
        },
        usedByFilters: true,
        syslogValue: 'Source_is_at_high_risk'
    },
    CRITICAL_RISK_SOURCE: {
        authTitle: 'Source is at Critical Risk',
        entityTitle: 'Source is at Critical Risk',
        insightsTitle: 'Source are at Critical Risk',
        riskRules: [RISK_RULES.CRITICAL_RISK_SOURCE],
        policyRule: RISK_RULES.CRITICAL_RISK_SOURCE,
        icons: {
            light: Icons.highRiskResourceIcon,
            dark: Icons.highRiskResourceIcon
        },
        usedByFilters: true,
        syslogValue: 'Source_is_at_critical_risk'
    },
    HIGH_RISK_RESOURCE: {
        authTitle: 'Resource is at High Risk',
        entityTitle: 'Resource is at High Risk',
        insightsTitle: 'Resources are at High Risk',
        riskRules: [RISK_RULES.HIGH_RISK_RESOURCE],
        icons: {
            light: Icons.highRiskResourceIcon,
            dark: Icons.highRiskResourceIcon
        },
        usedByFilters: true,
        syslogValue: 'Resource_is_at_high_risk'
    },
    CRITICAL_RISK_RESOURCE: {
        authTitle: 'Resource is at Critical Risk',
        entityTitle: 'Resource is at Critical Risk',
        insightsTitle: 'Resources are at Critical Risk',
        riskRules: [RISK_RULES.CRITICAL_RISK_RESOURCE],
        icons: {
            light: Icons.highRiskResourceIcon,
            dark: Icons.highRiskResourceIcon
        },
        usedByFilters: true,
        syslogValue: 'Resource_is_at_critical_risk'
    },
    SHARED_USER: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Shared Account',
        entityTitle: 'Shared Account',
        insightsTitle: 'Shared Accounts',
        description:
            "Active directory user accounts that are used from multiple devices and servers at the same time suggesting that they are shared by separate human users. These user accounts are important to monitor and protect, since there is no single accountable employee for these accounts and in case of a breach it's hard to trace back the actual source when shared users are being used. These accounts also tend to have greater privileges since they are used in different contexts.",
        mitigation:
            'Minimize the use of shared accounts and require strong passwords for accounts that must be shared.',
        reference: {
            desc: 'MITRE Attack TA0006 - Credential Access',
            link: 'https://attack.mitre.org/tactics/TA0006/'
        },
        riskRules: [RISK_RULES.SHARED_USER],
        policyRule: RISK_RULES.SHARED_USER,
        customInsightRule: RISK_RULES.SHARED_USER,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            light: Icons.lightSharedUserIcon,
            dark: Icons.sharedUserIcon
        },
        usedByFilters: true,
        syslogValue: 'Shared_account',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    SITE_ABNORMALITY: {
        type: INDICATOR_TYPES.ABNORMALITIES,
        authTitle: 'Abnormal Site',
        entityTitle: 'Abnormal Site',
        insightsTitle: 'Abnormal Sites',
        mitigation:
            'An abnormal site does not always indicate a suspected attack. Therefore, determine whether this "abnormal site" is really a suspected attack.',
        reference: {
            desc: 'MITRE Attack T1584.004 - Compromise Infrastructure: Server',
            links: 'https://attack.mitre.org/techniques/T1584/004/'
        },
        riskRules: [RISK_RULES.SITE_ABNORMALITY],
        policyRule: RISK_RULES.SITE_ABNORMALITY,
        extraLogDetails: {
            extraDetailsType: 'string',
            extraDetailsField: 'site'
        },
        icons: {
            light: Icons.geoLocationAnomalyIcon,
            dark: Icons.geoLocationAnomalyIcon
        },
        usedByFilters: true,
        syslogValue: 'Abnormal_site',
        customInsightRule: RISK_RULES.SITE_ABNORMALITY
    },
    SMB_ENUMERATION: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspected SMB Enumeration',
        entityTitle: 'Suspected SMB Enumeration',
        insightsTitle: 'Suspected SMB Enumeration',
        description:
            'SMB enumeration is an attack method in which attackers enumerate remote file servers using SMB, to search for files containing credentials & other sensitive information. The Risk Indicator indicates the user was suspected of this activity.',
        mitigation: 'Disable accounts that performed the attack, and reset their passwords.',
        reference: {
            desc: 'MITRE Attack T1135 - Network Share Discovery',
            link: 'https://attack.mitre.org/techniques/T1135/'
        },
        riskRules: [RISK_RULES.SMB_ENUMERATION],
        policyRule: RISK_RULES.SMB_ENUMERATION,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            light: Icons.lightSmbEnumerationIcon,
            dark: Icons.smbEnumerationIcon
        },
        reason: {
            template:
                '${entityName} authenticated between ${start_time} and ${end_time}, to at least ${servers_count} servers, using the CIFS (Common Internet File System) service class.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                servers_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER
            }
        },
        usedByFilters: true,
        syslogValue: 'Suspected_SMB_Enumeration'
    },
    STALE_USER: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Stale Account',
        authTitle: 'Stale Account',
        insightsTitle: 'Stale Accounts',
        description:
            'Accounts who have not authenticated using Silverfort in the last year. Stale accounts, such as employees who left the company whose credentials were not disabled, could be used by an attacker. It is recommended to disable stale accounts.',
        mitigation:
            'Disable and then delete stale users.\n' +
            'Process:\n' +
            '1. Open Active Directory Users and Computers (dsa.msc).\n' +
            '2. Locate the stale users in the Users or Organizational Unit (OU).\n' +
            '3. Right-click each user, then select Disable Account.\n' +
            '(Optional) Move the users to a Disabled Users OU for further review.\n' +
            'Note! We recommend notifying managers and HR about stale users before deletion, and verifying that no critical system dependencies exist (e.g. linked exchange, VPN, or legacy systems). We also recommend keeping disabled users for 30+ days before final deletion.\n' +
            '4. Delete Stale Users Permanently- Access ADUC, and navigate to the Disabled Users OU (that you created in the previous step). Right-click each stale user, the select Delete.',
        riskReportMitigation:
            'Delete unnecessary accounts and service accounts. It is better to delete the account than to disable it, as an attacker can always re-enable them any time. In addition, verify that users and service accounts have the least privileges necessary to function.',
        reference: {
            desc: 'MITRE Attack T1078 - Valid Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/'
        },
        riskRules: [RISK_RULES.STALE_USER],
        customInsightRule: RISK_RULES.STALE_USER,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            light: Icons.lightStaleUserIcon,
            dark: Icons.staleUserIcon
        },
        reason: {
            template:
                'The account ${entityName} has not been used for more than ${stale_days_count} days. Last logon time was on ${last_successful_login}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                stale_days_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                last_successful_login: INSIGHTS_FIELD_TYPES.RAW_FIELDS.OPTIONAL_DATE
            }
        },
        extraEntityDetails: [
            {
                moreDetailsKey: 'last_successful_login',
                moreDetailsHeader: 'Last successful login',
                moreDetailsFormat: 'date'
            }
        ],
        usedByFilters: true,
        sendToSplunk: true,
        syslogValue: 'Stale_user',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    SERVER_BRUTEFORCE: {
        authTitle: 'Suspected Server Brute Force',
        entityTitle: 'Suspected Server Brute Force',
        insightsTitle: 'Suspected Servers Brute Force',
        description:
            'Adversaries use brute force techniques to gain access to accounts when passwords are unknown or when password hashes are obtained. Without knowledge of the password for an account or set of accounts, an adversary may systematically guess the password using a repetitive or iterative mechanism. Attackers can use brute force methods by interacting with a service that will check the validity of those credentials. It can also work offline against previously acquired credential data, such as password hashes.',
        mitigation:
            'Check whether the user is running a script using the wrong credentials, and, If so, reset the account password and investigate how a potential attacker could have retrieved the password.',
        riskRules: [RISK_RULES.SERVER_BRUTEFORCE, RISK_RULES.SERVER_BRUTEFORCE_RULE],
        policyRule: RISK_RULES.SERVER_BRUTEFORCE,
        customInsightRule: RISK_RULES.SERVER_BRUTEFORCE_RULE,
        icons: {
            dark: Icons.bruteForceIcon,
            light: Icons.bruteForceIcon
        },
        reason: {
            template:
                'There were at least ${failed_authentications_count} failed authentication to ${entityName}, between ${start_time} and ${end_time}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                failed_authentications_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        usedByFilters: true,
        syslogValue: 'Suspected_brute_force_attack_on_the_server',
        adEntityType: AD_ENTITY_TYPES.SERVER
    },
    USE_OF_STALE_USER: {
        authTitle: 'Stale User Activity',
        entityTitle: 'Stale User Activity',
        insightsTitle: 'Stale User Activity',
        reference: {
            desc: 'MITRE Attack T1078 - Valid Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/'
        },
        riskRules: [RISK_RULES.USE_OF_STALE_USER],
        policyRule: RISK_RULES.USE_OF_STALE_USER,
        icons: {
            light: Icons.lightStaleUserIcon,
            dark: Icons.staleUserIcon
        },
        usedByFilters: true,
        syslogValue: 'Stale_user_activity'
    },
    SHADOW_ADMIN: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Shadow Admin',
        authTitle: 'Shadow Admin',
        insightsTitle: 'Shadow Admins',
        description:
            'Shadow Admins are user accounts in your network that have sensitive privileges. Shadow Admins are typically overlooked because they are not members of a privileged Active Directory group, but instead are given privileges through the direct assignment of permissions, using ACLs on AD objects.',
        mitigation:
            'Enforce least privilege principles by confirming only authorized users have admin priveleges. \n' +
            'Recommendations:\n' +
            "- Use Silverfort's Just-in-Time Admin Access feature (in the PAS module) to grant temporary admin rights.\n" +
            '- Review ACLs and Delegation regularly, to keep permissions minimal.',
        reference: {
            desc: 'MITRE Attack T1078 - Valid Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        riskRules: [RISK_RULES.SHADOW_ADMIN],
        policyRule: RISK_RULES.SHADOW_ADMIN,
        customInsightRule: RISK_RULES.SHADOW_ADMIN,
        icons: {
            light: Icons.lightShadowAdminIcon,
            dark: Icons.shadowAdminIcon
        },
        usedByFilters: true,
        sendToSplunk: true,
        syslogValue: 'Shadow_admin',
        isPostureInsight: true,
        category: RISK_CATEGORIES.PRIVILEGE_ESCALATION,
        severity: RISK_SEVERITY.CRITICAL
    },
    STALE_RESOURCE: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Stale Device',
        authTitle: 'Stale Device',
        insightsTitle: 'Stale Devices',
        description: 'List of devices that have not been used in the last year (or configurable timeframe)',
        mitigation:
            '1. Delete unnecessary users and service accounts. It is better to delete accounts than to disable them, as an attacker can always re-enable accounts at any time.\n2. Verify that users and service accounts have the least amount of privileges necessary to function.',
        reference: {
            desc: 'MITRE Attack T1078 - Valid Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/'
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        riskRules: [RISK_RULES.STALE_RESOURCE],
        policyRule: RISK_RULES.STALE_RESOURCE,
        customInsightRule: RISK_RULES.STALE_RESOURCE,
        icons: {
            light: Icons.lightStaleResourceIcon,
            dark: Icons.staleResourceIcon
        },
        reason: {
            template:
                'The resource ${entityName} has not been used for more than ${stale_days_count} days. Last logon time was on ${last_successful_login}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                stale_days_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                last_successful_login: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        extraEntityDetails: [
            {
                moreDetailsKey: 'last_successful_login',
                moreDetailsHeader: 'Last successful login',
                moreDetailsFormat: 'date'
            }
        ],
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Stale_resource',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    STALE_DOMAIN_CONTROLLER: {
        entityTitle: 'Dormant DC',
        authTitle: 'Dormant DC',
        insightsTitle: 'Dormant DC',
        description: 'DCs that haven’t authenticated to the domain for an extended period of time.',
        mitigation:
            'Review the DC to determine whether it is still in use:\n' +
            '- If it has been intentionally decommissioned > remove its computer object from Active Directory.\n' +
            '- If it is still operational > investigate why it has not authenticated and restore connectivity or functionality as needed.\n' +
            'Regularly monitor DC activity to detect drift, misconfiguration, or unauthorized changes.',
        reference: {
            desc: 'MITRE Attack T1078 - Valid Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/'
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        riskRules: [RISK_RULES.STALE_DOMAIN_CONTROLLER],
        policyRule: RISK_RULES.STALE_DOMAIN_CONTROLLER,
        customInsightRule: RISK_RULES.STALE_DOMAIN_CONTROLLER,
        icons: {
            light: Icons.staleDomainController,
            dark: Icons.staleDomainController
        },
        reason: {
            template:
                'The DC ${entityName} has not authenticated to the domain for over ${stale_days_count} days. Last authentication ${last_successful_login}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                stale_days_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                last_successful_login: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        extraEntityDetails: [
            {
                moreDetailsKey: 'last_successful_login',
                moreDetailsHeader: 'Last successful login',
                moreDetailsFormat: 'date'
            }
        ],
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Stale_domain_controller',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    DOMAIN_CONTROLLER_OLD_PASSWORD: {
        entityTitle: 'DC with Old Password',
        authTitle: 'DC with Old Password',
        insightsTitle: 'DC with Old Password',
        description: 'DCs using passwords that haven’t been changed for an extended period of time, increasing the risk of credential reuse, brute-force, or offline attacks.',
        mitigation:
            '1, Change the DC’s password\n' +
            '2. Implement a security policy to regularly audit and rotate credentials for all high-privilege accounts.',
        reference: {
            desc: 'MITRE Attack T1098 - Account Manipulation',
            link: 'https://attack.mitre.org/techniques/T1098'
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        riskRules: [RISK_RULES.DOMAIN_CONTROLLER_OLD_PASSWORD],
        policyRule: RISK_RULES.DOMAIN_CONTROLLER_OLD_PASSWORD,
        customInsightRule: RISK_RULES.DOMAIN_CONTROLLER_OLD_PASSWORD,
        icons: {
            light: Icons.domainControllerOldPassword,
            dark: Icons.domainControllerOldPassword
        },
        reason: {
            template:
                'The DC ${entityName} password has not been changed for ${days_threshold} days. Last password change ${last_reset_time}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                days_threshold: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                last_reset_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        extraEntityDetails: [
            {
                moreDetailsKey: 'last_reset_time',
                moreDetailsHeader: 'Last reset time',
                moreDetailsFormat: 'date'
            }
        ],
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Domain_controller_with_old_password',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    SHARED_DEVICE: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Shared Device',
        authTitle: 'Shared Device',
        insightsTitle: 'Shared Devices',
        description:
            "Devices that are used by multiple users, it's important to monitor these devices since these have NTLM hashed of multiple different users stored within their LSASS (Local Security Authority Subsystem Service) allowing an attacker to use these credentials to laterally move in the network.",
        mitigation:
            "Increase the shared device's security.\n" +
            'Recommendations:\n' +
            '- Require unique user profiles for each session.\n' +
            '- Apply automatic session timeouts to lock devices after inactivity.\n' +
            '- Implement MFA for the device using a Silverfort policy.',
        reference: {
            desc: 'MITRE Attack T1135 - Network Share Discovery',
            link: 'https://attack.mitre.org/techniques/T1135/'
        },
        riskRules: [RISK_RULES.SHARED_DEVICE],
        policyRule: RISK_RULES.SHARED_DEVICE,
        customInsightRule: RISK_RULES.SHARED_DEVICE,
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            light: Icons.lightSharedDeviceIcon,
            dark: Icons.sharedDeviceIcon
        },
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Shared_device',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.LOW
    },
    HOST_BRUTEFORCE: {
        authTitle: 'Suspected Brute Force from Host',
        entityTitle: 'Suspected Brute Force from Host',
        insightsTitle: 'Suspected Brute Force from Hosts',
        description:
            'Adversaries use brute force techniques to gain access to accounts when passwords are unknown or when password hashes are obtained. Without knowledge of the password for an account or set of accounts, an adversary may systematically guess the password using a repetitive or iterative mechanism. Attackers can use brute force methods by interacting with a service that will check the validity of those credentials. It can also work offline against previously acquired credential data, such as password hashes.',
        mitigation:
            'Check whether the user is running a script using the wrong credentials, and, If so, reset the account password and investigate how a potential attacker could have retrieved the password.',
        reference: {
            desc: 'MITRE Attack T1110 - Brute Force',
            link: 'https://attack.mitre.org/techniques/T1110/'
        },
        riskRules: [RISK_RULES.HOST_BRUTEFORCE],
        policyRule: RISK_RULES.HOST_BRUTEFORCE,
        customInsightRule: RISK_RULES.HOST_BRUTEFORCE,
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            dark: Icons.bruteForceIcon,
            light: Icons.bruteForceIcon
        },
        reason: {
            template:
                'There were at least ${failed_authentications_count} failed authentication from ${entityName}, between ${start_time} and ${end_time}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                failed_authentications_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        usedByFilters: true,
        syslogValue: 'Suspected_brute_force_attack_from_the_host'
    },
    MIXED_SESSION_HOST: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Host with Privilege Escalation Path',
        entityTitle: 'Host with Privilege Escalation Path',
        insightsTitle: 'Hosts with Privilege Escalation Path',
        description:
            'When a machine is accessed by both unprivileged and highly privileged users, this poses a risk where session tickets or cached credentials are left behind on the machines which leaves a path for an unauthorized user logging in to this machine to collect those credentials and escalate themselves to a high privilege permissions.',
        mitigation:
            'Block escalation path.\n' +
            'Recommendations:\n' +
            '- Configure regular credential flushing and limit the number of cached credentials.\n' +
            '- Enforce least privilege principles by restricting the use of highly privileged users to only certain tasks and machines.\n' +
            '- Create a Silverfort MFA policy for all users outside of a certain group for sensitive hosts.',
        reference: {
            desc: 'Mitre Attack T1003 - Cached Domain Credentials',
            link: 'https://attack.mitre.org/techniques/T1003/005/'
        },
        riskRules: [RISK_RULES.MIXED_SESSION_HOST],
        customInsightRule: RISK_RULES.MIXED_SESSION_HOST,
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            dark: Icons.mixedSessionHostIcon,
            light: Icons.mixedSessionHostIcon
        },
        reason: {
            template:
                'Privileged account ${privileged_user_guid} accessed host ${entityName} on ${privileged_user_auth_time} and non-privileged account ${unprivileged_user_guid} accessed it on ${unprivileged_user_auth_time}. Possible credential exposure.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                privileged_user_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                privileged_user_auth_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                unprivileged_user_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                unprivileged_user_auth_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        },
        syslogValue: 'Host_with_privilege_escalation_path',
        usedByFilters: true,
        isPostureInsight: true,
        category: RISK_CATEGORIES.PRIVILEGE_ESCALATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    FAILED_AUTHS_BATCH: {
        authTitle: 'Failed Authentication Batch',
        entityTitle: 'Failed Authentication Batch',
        insightsTitle: 'Failed Authentication Batch',
        mitigation:
            'Investigate and add preventative measures.\n' +
            'Process:\n' +
            '1. Review authentication logs to identify which users, accounts, or systems are involved in the failed attempts.\n' +
            '2. Confirm users / services are using correct authentication credentials.\n' +
            '3. Look for any unusual patterns or sources in these failed authentications that resemble attack techniques.\n' +
            '4. Set thresholds for the number of failed authentications, to prevent brute force attacks.\n' +
            '5. Require MFA with Silverfort policies, for an additional extra layer of protection.',
        description:
            'Significant amount of failed authentications in a short period of time for a specific user indicates that there might be a misconfiguration, password change or other cause which lead to this amount of failures. Deeper investigation of such users is required in order to eliminate the possibility of Brute Force attack attempts.',
        riskRules: [RISK_RULES.FAILED_AUTHS_BATCH],
        policyRule: RISK_RULES.FAILED_AUTHS_BATCH,
        customInsightRule: RISK_RULES.FAILED_AUTHS_BATCH,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.userRiskIcon,
            light: Icons.userRiskIcon
        },
        usedByFilters: true,
        syslogValue: 'Failed_Authentications_Batch',
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.LOW
    },
    SERVICE_ACCOUNT: {
        authTitle: 'Suspected Service Account',
        entityTitle: 'Suspected Service Account',
        insightsTitle: 'Suspected Service Accounts',
        riskRules: [RISK_RULES.SERVICE_ACCOUNT],
        customInsightRule: RISK_RULES.SERVICE_ACCOUNT,
        icons: {
            light: Icons.lightServiceAccountIcon,
            dark: Icons.serviceAccountIcon
        },
        usedByFilters: true,
        syslogValue: 'Suspected_service_account',
        adEntityType: AD_ENTITY_TYPES.USER
    },
    HIGH_RISK_USER: {
        authTitle: 'User is at High Risk',
        entityTitle: 'User is at High Risk',
        insightsTitle: 'Users are at High Risk',
        riskRules: [RISK_RULES.HIGH_RISK_USER],
        icons: {
            light: Icons.userRiskIcon,
            dark: Icons.userRiskIcon
        },
        usedByFilters: true,
        syslogValue: 'User_is_at_high_risk'
    },
    CRITICAL_RISK_USER: {
        authTitle: 'User is at Critical Risk',
        entityTitle: 'User is at Critical Risk',
        insightsTitle: 'Users are at Critical Risk',
        riskRules: [RISK_RULES.CRITICAL_RISK_USER],
        icons: {
            light: Icons.userRiskIcon,
            dark: Icons.userRiskIcon
        },
        usedByFilters: true,
        syslogValue: 'User_is_at_critical_risk'
    },
    PRIVILEGED_USER_NOT_IN_PROTECTED_USERS_GROUP: {
        entityTitle: 'Privileged User Not in Protected Group',
        authTitle: 'Privileged User Not in Protected Group',
        insightsTitle: 'Privileged User Not in Protected Group',
        description:
            'Privileged users outside the Protected Users group are vulnerable to credential caching and weaker protocols like NTLM, increasing the risk of compromise.',
        mitigation: 'Add the user to the Protected Users group or reduce the user’s privileges.',
        adEntityType: AD_ENTITY_TYPES.USER,
        riskRules: [RISK_RULES.PRIVILEGED_USER_NOT_IN_PROTECTED_USERS_GROUP],
        policyRule: RISK_RULES.PRIVILEGED_USER_NOT_IN_PROTECTED_USERS_GROUP,
        customInsightRule: RISK_RULES.PRIVILEGED_USER_NOT_IN_PROTECTED_USERS_GROUP,
        icons: {
            light: Icons.privilegedUserNotInProtectedUsersGroupIcon,
            dark: Icons.privilegedUserNotInProtectedUsersGroupIcon
        },
        usedByFilters: true,
        syslogValue: 'Privileged_user_not_in_protected_group_controller',
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.MEDIUM
    },
    LOCKED_USER: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Locked Account',
        authTitle: 'Locked Account',
        insightsTitle: 'Locked Accounts',
        description:
            'Users who have been locked by Active Directory. An account could become locked if an incorrect password was entered too many times. This could indicate an attack trying to enumerate or guess the password. User account will be considered as locked until its first successful authentication after being unlocked.',
        mitigation:
            '1. Update or remove any systems that are using invalid credentials.\n' +
            '2. Unlock the account after the issue is fixed.\n' +
            '3. Monitor for repeated lockouts, which may indicate automation issues or attack attempts.',
        reference: {
            desc: 'MITRE Attack T1531 - Account Access Removal',
            link: 'https://attack.mitre.org/techniques/T1531/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        sendToSplunk: true,
        icons: {
            light: Icons.lightLockedAccountIcon,
            dark: Icons.lockedAccountIcon
        },
        riskRules: [RISK_RULES.LOCKED_USER],
        customInsightRule: RISK_RULES.LOCKED_USER,
        usedByFilters: true,
        syslogValue: 'User_locked',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.LOW
    },
    USER_WITH_SPN: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Account with SPN',
        authTitle: 'Account with SPN',
        insightsTitle: 'Accounts with SPN',
        description: 'Accounts with SPN are susceptible to exploitation through Kerberoasting.',
        mitigation:
            '1. Remove unnecessary SPN to reduce attack surface.\n2. Use strong, unique passwords for accounts where SPN cannot be removed.',
        riskRules: [RISK_RULES.USER_WITH_SPN],
        policyRule: RISK_RULES.USER_WITH_SPN,
        customInsightRule: RISK_RULES.USER_WITH_SPN,
        icons: {
            light: Icons.userWithSpnIcon,
            dark: Icons.userWithSpnIcon
        },
        usedByFilters: true,
        syslogValue: 'User_with_SPN',
        reason: {
            template: 'Account ${entityName} is associated with the SPNs: ${spns}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                spns: INSIGHTS_FIELD_TYPES.RAW_FIELDS.PRETTY_LIST
            }
        },
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.MEDIUM,
        isPostureInsight: true
    },
    UNFAMILIAR_SIGN_IN: {
        type: INDICATOR_TYPES.ABNORMALITIES,
        authTitle: 'Unfamiliar Sign-in',
        entityTitle: 'Unfamiliar Sign-in',
        insightsTitle: 'Unfamiliar Sign-in',
        reference: {
            desc: 'MITRE Attack T1078.004 - Valid Accounts: Cloud Accounts',
            link: 'https://attack.mitre.org/techniques/T1078/004/'
        },
        riskRules: [RISK_RULES.UNFAMILIAR_SIGN_IN],
        icons: {
            light: Icons.unfamiliarSignInIcon,
            dark: Icons.unfamiliarSignInIcon
        },
        usedByFilters: true,
        syslogValue: 'Unfamiliar_sign-in'
    },
    WEAKLY_ENCRYPTED_TICKET: {
        authTitle: 'Weakly Encrypted Ticket',
        entityTitle: 'Weakly Encrypted Ticket',
        insightsTitle: 'Weakly Encrypted Ticket',
        riskRules: [RISK_RULES.WEAKLY_ENCRYPTED_TICKET],
        policyRule: RISK_RULES.WEAKLY_ENCRYPTED_TICKET,
        extraLogDetails: {
            extraDetailsType: 'encryption_type',
            extraDetailsField: 'krb_params.response_ticket_enc_type'
        },
        usedByFilters: true,
        syslogValue: 'Weakly_encrypted_ticket'
    },
    WEAKLY_ENCRYPTED_MESSAGE: {
        authTitle: 'Weakly Encrypted Reply',
        entityTitle: 'Weakly Encrypted Reply',
        insightsTitle: 'Weakly Encrypted Reply',
        riskRules: [RISK_RULES.WEAKLY_ENCRYPTED_MESSAGE],
        policyRule: RISK_RULES.WEAKLY_ENCRYPTED_MESSAGE,
        extraLogDetails: {
            extraDetailsType: 'encryption_type',
            extraDetailsField: 'krb_params.response_enc_type'
        },
        usedByFilters: true,
        syslogValue: 'Weakly_encrypted_reply'
    },
    USER_WEAK_ENCRYPTION: {
        entityTitle: 'User Authenticated Frequently with Weak Encryption',
        authTitle: 'User Authenticated Frequently with Weak Encryption',
        insightsTitle: 'Weak Encryption (Users)',
        description:
            'Use of weak encryption to secure user-sensitive information (such as NTLM hashes) exposes accounts to attack scenarios in which user credentials are obtained (for example by enumeration). Once obtained, attackers can change the password and then use the credentials to access sensitive resources without detection',
        mitigation:
            'Bolster passowrd security.\n' +
            'Recommendations:\n' +
            '- Require strong passwords.\n' +
            "- Disable weak encryption for applications that don't require it.",
        reference: {
            desc: 'MITRE Attack T1558 - Steal or Forge Kerberos Tickets',
            link: 'https://attack.mitre.org/techniques/T1558/'
        },
        riskRules: [RISK_RULES.USER_WEAK_ENCRYPTION],
        policyRule: RISK_RULES.USER_WEAK_ENCRYPTION,
        customInsightRule: RISK_RULES.USER_WEAK_ENCRYPTION,
        icons: {
            light: Icons.lightWeakEncryptionIcon,
            dark: Icons.weakEncryptionIcon
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        usedByFilters: true,
        syslogValue: 'User_authenticated_frequently_with_weak_encryption',
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.LOW,
        type: INDICATOR_TYPES.EXPOSURE
    },
    SERVER_WEAK_ENCRYPTION: {
        entityTitle: 'Weak Encryption Frequently Used in the Server',
        authTitle: 'Weak Encryption Frequently Used in the Server',
        insightsTitle: 'Weak Encryption (Servers)',
        description:
            'Use of weak encryption to secure user-sensitive information (such as NTLM hashes) exposes accounts to attack scenarios in which user credentials are obtained (for example by enumeration). Once obtained, attackers can change the password and then use the credentials to access sensitive resources without detection',
        mitigation:
            'Bolster passowrd security.\n' +
            'Recommendations:\n' +
            '- Require strong passwords.\n' +
            "- Disable weak encryption for applications that don't require it.",
        reference: {
            desc: 'MITRE Attack T1558 - Steal or Forge Kerberos Tickets',
            link: 'https://attack.mitre.org/techniques/T1558/'
        },
        riskRules: [RISK_RULES.SERVER_WEAK_ENCRYPTION],
        policyRule: RISK_RULES.SERVER_WEAK_ENCRYPTION,
        customInsightRule: RISK_RULES.SERVER_WEAK_ENCRYPTION,
        icons: {
            light: Icons.lightWeakEncryptionIcon,
            dark: Icons.weakEncryptionIcon
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Weak_encryption_frequently_used_in_the_server',
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.MEDIUM,
        type: INDICATOR_TYPES.EXPOSURE
    },
    LDAP_OBSOLETE_TLS: {
        authTitle: 'Obsolete TLS',
        riskRules: [RISK_RULES.LDAP_OBSOLETE_TLS],
        policyRule: RISK_RULES.LDAP_OBSOLETE_TLS,
        icons: {
            light: Icons.lightWeakEncryptionIcon,
            dark: Icons.weakEncryptionIcon
        },
        extraLogDetails: {
            extraDetailsType: 'tls_type',
            extraDetailsField: 'ldap_params.negotiation_protocol'
        },
        usedByFilters: true,
        syslogValue: 'Obsolete_TLS'
    },
    LDAP_WEAK_CIPHER: {
        authTitle: 'Weak Cipher',
        insightsTitle: 'LDAPS weak encryption',
        riskRules: [RISK_RULES.LDAP_WEAK_CIPHER],
        policyRule: RISK_RULES.LDAP_WEAK_CIPHER,
        icons: {
            light: Icons.lightWeakEncryptionIcon,
            dark: Icons.weakEncryptionIcon
        },
        extraLogDetails: {
            extraDetailsType: 'cipher_type',
            extraDetailsField: 'ldap_params.cipher'
        },
        syslogValue: 'Weak_Cipher'
    },
    LDAP_ANONYMOUS_BIND: {
        authTitle: 'Anonymous LDAP Authentication',
        riskRules: [RISK_RULES.LDAP_ANONYMOUS_BIND],
        policyRule: RISK_RULES.LDAP_ANONYMOUS_BIND,
        usedByFilters: true,
        syslogValue: 'anonymous_LDAP_bind_Auth',
        icons: {
            light: Icons.siren,
            dark: Icons.siren
        }
    },
    USE_OF_BUILT_IN_GUEST: {
        authTitle: 'Login by Built-in Guest Account',
        entityTitle: 'Login by Built-in Guest Account',
        insightsTitle: 'Login by Built-in Guest Accounts',
        riskRules: [RISK_RULES.USE_OF_BUILT_IN_GUEST],
        policyRule: RISK_RULES.USE_OF_BUILT_IN_GUEST,
        icons: {
            light: Icons.BuiltInGuestIcon,
            dark: Icons.BuiltInGuestIcon
        },
        usedByFilters: true,
        syslogValue: 'Login_by_built-in_Guest_account'
    },
    USE_OF_BUILT_IN_ADMIN: {
        authTitle: 'Login by Built-in Administrator Account',
        entityTitle: 'Login by Built-in Administrator Account',
        insightsTitle: 'Login by Built-in Administrator Accounts',
        riskRules: [RISK_RULES.USE_OF_BUILT_IN_ADMIN],
        policyRule: RISK_RULES.USE_OF_BUILT_IN_ADMIN,
        icons: {
            light: Icons.BuiltInAdminIcon,
            dark: Icons.BuiltInAdminIcon
        },
        usedByFilters: true,
        syslogValue: 'Login_by_built-in_Administrator_account'
    },
    HISTORIC_ENCRYPTION: {
        authTitle: 'Historic Encryption Type',
        entityTitle: 'Historic Encryption Type',
        insightsTitle: 'Historic Encryption Types',
        description:
            "Silverfort identifies authentications with RC4-MD4 or RC4-HMAC-OLD encryption types which are considered weak. This may indicate an exploitation of vulnerabilities CVE-2022-33647 or CVE-2022-33679. By exploiting these vulnerabilities an unauthenticated attacker could perform a man-in-the-middle attack, downgrade a client's encryption, and crack the user's cypher key. The attacker could then compromise the user's Kerberos session key to elevate privileges.",
        mitigation:
            'Customers running Windows Server 2012 and above, and who use Kerberos Armoring, (known as Flexible Authentication Secure Tunneling -FAST), are protected from this vulnerability (Windows Kerberos elevation of privilege vulnerability.).',
        riskRules: [RISK_RULES.HISTORIC_ENCRYPTION],
        policyRule: RISK_RULES.HISTORIC_ENCRYPTION,
        icons: {
            light: Icons.HistoricEncryptionIcon,
            dark: Icons.HistoricEncryptionIcon
        },
        usedByFilters: true,
        shownForKRBTGT: true,
        syslogValue: 'Historic_Encryption_Type'
    },
    PRIVILEGED_COMPUTER: {
        authTitle: 'Computer Account in Privileged Groups',
        entityTitle: 'Computer Account in Privileged Groups',
        insightsTitle: 'Computer Accounts in Privileged Groups',
        description:
            'Machine accounts that are members of domain privileged groups are susceptible to impersonation by malicious actors, providing them with privileged access to the domain.',
        mitigation: 'Restrict machine account to least privilege permissions.',
        reason: {
            template:
                'Machine account ${entityName} is a member of ${privileged_groups}, creating a risk of abuse for privilege escalation or persistence.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                privileged_groups: INSIGHTS_FIELD_TYPES.RAW_FIELDS.PRETTY_LIST
            }
        },
        riskRules: [RISK_RULES.PRIVILEGED_COMPUTER],
        policyRule: RISK_RULES.PRIVILEGED_COMPUTER,
        customInsightRule: RISK_RULES.PRIVILEGED_COMPUTER,
        icons: {
            light: Icons.RiskyResourceIcon,
            dark: Icons.RiskyResourceIcon
        },
        usedByFilters: true,
        syslogValue: 'Computer_Account_in_Privileged_Group',
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM,
        isPostureInsight: true,
        type: INDICATOR_TYPES.EXPOSURE
    },
    RPC_SEALING_NOT_ENCRYPTED: {
        authTitle: 'Suspected Netlogon RPC Privilege Elevation',
        entityTitle: 'Suspected Netlogon RPC Privilege Elevation',
        insightsTitle: 'Suspected Netlogon RPC Privilege Elevation',
        description:
            'An authenticated attacker could leverage cryptographic protocol vulnerabilities in the Windows Netlogon protocol when RPC Signing is used instead of RPC Sealing. Where RPC Signing is used instead of RPC Sealing the attacker could gain control of the service and then might be able to modify Netlogon protocol traffic to elevate their privileges.',
        reference: {
            desc: 'CVE-2022-38023',
            link: 'https://cve.mitre.org/cgi-bin/cvename.cgi?name=CVE-2022-38023'
        },
        riskRules: [RISK_RULES.RPC_SEALING_NOT_ENCRYPTED],
        policyRule: RISK_RULES.RPC_SEALING_NOT_ENCRYPTED,
        customInsightRule: RISK_RULES.RPC_SEALING_NOT_ENCRYPTED,
        icons: {
            light: Icons.UserHackIcon,
            dark: Icons.UserHackIcon
        },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${host_guid} to ${server_guid} using RPC signing instead of RPC sealing.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                server_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID
            }
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        usedByFilters: true,
        syslogValue: 'Suspected_Netlogon_RPC_Privilege_Elevation'
    },
    PRINT_NIGHTMARE_FIX: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Print Nightmare Artifact Fix',
        entityTitle: 'Print Nightmare Artifact Fix',
        insightsTitle: 'Print Nightmare Artifact Fix',
        description:
            'A fix to the critical vulnerability named “Print Nightmare”, can cause print spooler clients to send bad authentication requests to an unknown SPN krbtgt/NT Authority, resulting in performance issues. Symptoms can include slow print jobs, slow DCs, and excessive Kerberos & NTLM traffic.',
        mitigation:
            'Under the registry key "HKEY_LOCAL_MACHINE\\SOFTWARE\\Policies\\Microsoft\\Windows NT\\Printers\\RPC", change RpcNamedPipeAuthentication to 0x02, for each device, or deploy it using group policy preference item.',
        reference: {
            link: 'https://techcommunity.microsoft.com/t5/ask-the-directory-services-team/a-print-nightmare-artifact-krbtgt-nt-authority/ba-p/3757962',
            desc: 'Print Nightmare Artifact Fix - krbtgt/NT Authority'
        },
        riskRules: [RISK_RULES.PRINT_NIGHTMARE_FIX],
        policyRule: RISK_RULES.PRINT_NIGHTMARE_FIX,
        customInsightRule: RISK_RULES.PRINT_NIGHTMARE_FIX,
        icons: {
            light: Icons.printNightmareFixDarkIcon,
            dark: Icons.printNightmareFixBrightIcon
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        usedByFilters: true,
        syslogValue: 'Print_Nightmare_Fix_Artifact',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.LOW
    },
    S4U2SELF: {
        authTitle: 'Service for User to Self (S4U2self) Extension',
        entityTitle: 'Service for User to Self (S4U2self) Extension',
        insightsTitle: 'Service for User to Self (S4U2self) Extension',
        description:
            "An extension that allows a service to obtain a Kerberos service ticket to itself.\n            The service ticket contains the user's groups and can therefore be used in authorization decisions",
        icons: {
            light: Icons.RiskAuthIcon,
            dark: Icons.RiskAuthIcon
        },
        riskRules: [RISK_RULES.S4U2SELF],
        policyRule: RISK_RULES.S4U2SELF,
        usedByFilters: true,
        syslogValue: 'Service_for_User_to_Self_(S4U2self)_extension'
    },
    S4U2PROXY: {
        authTitle: 'Service for User to Proxy (S4U2proxy) Extension',
        entityTitle: 'Service for User to Proxy (S4U2proxy) Extension',
        insightsTitle: 'Service for User to Proxy (S4U2proxy) Extension',
        description:
            'An extension that allows a service to obtain a service ticket on behalf of a user to a different service',
        icons: {
            light: Icons.RiskAuthIcon,
            dark: Icons.RiskAuthIcon
        },
        riskRules: [RISK_RULES.S4U2PROXY],
        policyRule: RISK_RULES.S4U2PROXY,
        usedByFilters: true,
        syslogValue: 'Service_for_User_to_Proxy_(S4U2proxy)_extension'
    },
    RODC_PRIVILEGED_CREDENTIALS: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Privileged Account Credentials in RODC',
        entityTitle: 'Privileged Account Credentials in RODC',
        insightsTitle: 'Privileged Account Credentials in RODC',
        description:
            "Read-Only Domain Controllers (RODCs) should replicate user passwords only for user accounts that are members of the Allowed RODC Password Replication Group, or are listed in the RODC account's msDS-RevealOnDemandGroup attribute.\nHowever, the RODC may authenticate or cache passwords of user accounts that are either not members of the Allowed RODC Password Replication Group, or are not listed in the RODC account's msDS-RevealOnDemandGroup attribute.",
        mitigation:
            'Remove privileged users and groups from RODC Password Replication Policy, to prevent secrets from being replicated to RODCs.\n' +
            'Recommendations:\n' +
            'RODCs tend to be less secure than read-write DCs, therefore we recommend changing the Replicating Directory Changes All permission (granted to the Enterprise Read-only Domain Controllers object) to Replicating Directory Changes.',
        reference: {
            desc: 'MITRE Attack TA0006 - Credential Access',
            link: 'https://attack.mitre.org/tactics/TA0006/'
        },
        riskRules: [RISK_RULES.RODC_PRIVILEGED_CREDENTIALS],
        customInsightRule: RISK_RULES.RODC_PRIVILEGED_CREDENTIALS,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.userRiskIcon,
            light: Icons.userRiskIcon
        },
        usedByFilters: true,
        syslogValue: 'Privileged_account_credential_added_to_RODC',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.HIGH
    },
    SAPPHIRE_TICKET: {
        authTitle: 'Suspected Sapphire Ticket',
        entityTitle: 'Suspected Sapphire Ticket',
        insightsTitle: 'Suspected Sapphire Ticket',
        description:
            'In a Sapphire Ticket attack, an attacker with the KRBTGT secret and the credentials of an unprivileged user, can forge a TGT of a privileged user to infiltrate your network and systems. The forged TGT is achieved by modifying an existing legitimate TGT, changing the identity and privileges in the original TGT.',
        mitigation:
            'Reset the built-in KRBTGT account password twice, to invalidate any existing Sapphire Tickets that have been created with the KRBTGT secret and any Kerberos tickets derived from it. For each domain, change the KRBTGT account password, force replication, and then change the password a second time. In addition, block both the target user and impersonated user who were involved in the attack. For ongoing defense, consider rotating the KRBTGT account password every 180 days.',
        reference: {
            link: 'https://www.thehacker.recipes/a-d/movement/kerberos/forged-tickets/sapphire',
            desc: 'Sapphire Tickets (The Hacker Recipes)'
        },
        riskRules: [RISK_RULES.SAPPHIRE_TICKET],
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.sapphireTicketIcon,
            light: Icons.sapphireTicketIcon
        },
        reason: {
            template:
                '${entityName} authenticated at ${time} from ${host_guid} to ${server_guid} using U2U authentication and the ENC-TKT-IN-SKEY option.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                host_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID,
                server_guid: INSIGHTS_FIELD_TYPES.REMOTE_FIELDS.GUID
            }
        },
        usedByFilters: true,
        syslogValue: 'Suspected_Sapphire_Ticket'
    },
    UNMATCHED_TGT: {
        authTitle: 'Unmatched TGT (Early Availability)',
        description:
            'Silverfort flags TGS requests that it can not match with a preceding TGT request, as potentially fraudulent. Unmatched tickets may be an indication of golden tickets, where adversaries possessing the password hash of the KRBTGT account create counterfeit Kerberos TGTs to act as any account within your Active Directory.',
        riskRules: [RISK_RULES.UNMATCHED_TGT],
        icons: {
            dark: Icons.unmatchedTicketIcon,
            light: Icons.unmatchedTicketIcon
        },
        usedByFilters: true
    },
    ACCOUNT_ENUMERATION: {
        authTitle: 'Account Enumeration Reconnaissance',
        entityTitle: 'Account Enumeration Reconnaissance',
        insightsTitle: 'Accounts Enumeration Reconnaissance',
        description:
            'In Account Enumeration Reconnaissance, an attacker tries to guess user names in the domain, using a collection of potential user names, or tools such as KrbGuess. The goal of this attack is to map the domain user accounts, as an intelligence insight that will be used for additional attacks.',
        mitigation:
            'Disable the suspected source of the Account Enumeration Reconnaissance and check if there is evidence of any attack or vulnerability in this source.',
        reference: {
            desc: 'MITRE Attack TA0043 - Reconnaissance',
            link: 'https://attack.mitre.org/tactics/TA0043/'
        },
        riskRules: [RISK_RULES.ACCOUNT_ENUMERATION],
        customInsightRule: RISK_RULES.ACCOUNT_ENUMERATION,
        icons: {
            light: Icons.reconnaissanceIcon,
            dark: Icons.reconnaissanceIcon
        },
        reason: {
            template:
                '${entityName} received failed authentications between ${start_time} and ${end_time}, by at least ${non_existing_users_count} non-existing users.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                start_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                end_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                non_existing_users_count: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.NUMBER
            }
        },
        adEntityType: AD_ENTITY_TYPES.SERVER
    },
    AZURE_SHADOW_ADMIN: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Entra ID Shadow Admins',
        authTitle: 'Entra ID Shadow Admins',
        insightsTitle: 'Entra ID Shadow Admins',
        description:
            'Entra ID Shadow Admins are user accounts in Azure environment that have sensitive privileges. Shadow Admins privileges are given through the direct assignment of permissions, and they can be detected based on the list of their privileged roles.',
        mitigation: 'Remove the privileged roles from the Shadow Admin accounts.',
        reference: {
            desc: 'MITRE Attack T1098/003 - Account Manipulation: Additional Cloud Roles',
            link: 'https://attack.mitre.org/techniques/T1098/003/'
        },
        riskRules: [RISK_RULES.AZURE_SHADOW_ADMIN],
        customInsightRule: RISK_RULES.AZURE_SHADOW_ADMIN,
        icons: {
            light: Icons.lightAzureShadowAdminIcon,
            dark: Icons.azureShadowAdminIcon
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        extraEntityDetails: [
            {
                moreDetailsKey: 'roles',
                moreDetailsHeader: 'Privileged Roles',
                moreDetailsFormat: 'list'
            }
        ],
        category: RISK_CATEGORIES.PRIVILEGE_ESCALATION,
        severity: RISK_SEVERITY.CRITICAL
    },
    AZURE_PRIVILEGED_GUEST: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Entra ID Privileged Guest',
        insightsTitle: 'Entra ID Privileged Guests',
        description:
            'Azure Active Directory (Entra ID) allows you to restrict what external guest users can access within your organization. By default, guest users have limited permissions in Entra ID, whereas member users have full user permissions. If a guest user requires additional privileges, you can assign them an Entra ID privileged role. However, granting such privileges to guest users can pose a security risk, so those users should be closely monitored.',
        mitigation:
            'Revoke privileges from guest user.\n' +
            'Process:\n' +
            '1. In the Entra ID console, navigate to the User Management page.\n' +
            '2. Locate the relevant user and remove its administrative role assignments.',
        reference: {
            desc: `Restrict guest user access permissions - Microsoft Entra ID`,
            link: `https://learn.microsoft.com/en-us/azure/active-directory/enterprise-users/users-restrict-guest-permissions`
        },
        riskRules: [RISK_RULES.AZURE_PRIVILEGED_GUEST],
        customInsightRule: RISK_RULES.AZURE_PRIVILEGED_GUEST,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.userRiskIcon,
            light: Icons.userRiskIcon
        },
        extraEntityDetails: [
            {
                moreDetailsKey: 'roles',
                moreDetailsHeader: 'Privileged Roles',
                moreDetailsFormat: 'list'
            }
        ],
        syslogValue: 'Azure_Privileged_Guest',
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.HIGH
    },
    ENTRA_ID_ADMIN: {
        entityTitle: 'Entra ID Admin',
        insightsTitle: 'Entra ID Admins',
        authTitle: 'Entra ID Admin',
        riskRules: [RISK_RULES.ENTRA_ID_ADMIN],
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.domainAdminIcon,
            light: Icons.domainAdminIcon
        },
        syslogValue: 'Entra_ID_Admin'
    },
    ENTRA_ID_SYNCED_ADMIN: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'Synced Admin Account',
        insightsTitle: 'Synced Admin Accounts',
        authTitle: 'Synced Admin Account',
        description:
            'Syncing on-premises admin accounts to your cloud IdP (Entra ID) poses a risk, as attackers could exploit less-privileged cloud accounts to compromise your on-premises environment.',
        mitigation:
            'To enhance security, avoid syncing high-privileged accounts to the cloud. If necessary, employ other security measures to mitigate the risk of on-cloud to on-prem environment pivoting, such as privileged access controls or conditional access policies.',
        reference: {
            desc: `Microsoft Entra Connect Sync: Configure filtering - Microsoft Entra ID`,
            link: `https://learn.microsoft.com/en-us/entra/identity/hybrid/connect/how-to-connect-sync-configure-filtering`
        },
        riskRules: [RISK_RULES.ENTRA_ID_SYNCED_ADMIN],
        customInsightRule: RISK_RULES.ENTRA_ID_SYNCED_ADMIN,
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            light: Icons.entraIDSyncedAdminIcon
        },
        syslogValue: 'Synced_Admin_Account',
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.HIGH
    },
    RECURRENT_FAILURES: {
        authTitle: 'Recurrent Failed Authentications',
        entityTitle: 'Recurrent Failed Authentications',
        insightsTitle: 'Recurrent Failed Authentications',
        mitigation:
            'Check whether the user is running a script using the wrong credentials, and, If so, reset the account password and investigate how a potential attacker could have retrieved the password.',
        reference: {
            desc: 'MITRE Attack T1110 - Brute Force',
            link: 'https://attack.mitre.org/techniques/T1110/'
        },
        riskRules: [RISK_RULES.RECURRENT_FAILURES]
    },
    UNCONSTRAINED_DELEGATION_RESOURCE: {
        authTitle: 'Unconstrained Delegation',
        entityTitle: 'Unconstrained Delegation',
        insightsTitle: 'Unconstrained Delegation (Servers)',
        description:
            'Unconstrained delegation allows a user or service to impersonate another user (i.e. to use the access permissions of another user, usually more elevated). This practice is very risky and, if abused, could enable an attacker to gain full access to the permissions of the impersonated user.',
        mitigation:
            '1. In Active Directory Users and Computers (ADUC), open the affected computer or service account.\n' +
            '2. Go to Properties > Delegation and clear the option "Trust this computer/user for delegation to any service (Kerberos only)".\n' +
            '3. If delegation is required, configure Constrained Delegation or Resource-Based Constrained Delegation (RBCD) instead.\n' +
            '4. Validate that the application is configurable to constrained delegation and apply the correct configuration.\n' +
            '5. Verify that the service continues to function after the change.',
        reference: {
            desc: 'MITRE Attack TA0004 - Privilege Escalation',
            link: 'https://attack.mitre.org/tactics/TA0004/'
        },
        riskRules: [RISK_RULES.UNCONSTRAINED_DELEGATION_RESOURCE],
        icons: {
            light: Icons.lightUnconstrainedDelegationIcon,
            dark: Icons.unconstrainedDelegationIcon
        },
        // Currently this RI is hidden from the insight page, meaning it will only be shown in the entity page
        adEntityType: AD_ENTITY_TYPES.USER,
        customInsightRule: RISK_RULES.UNCONSTRAINED_DELEGATION_RESOURCE
    },
    UNCONSTRAINED_DELEGATION_USER: {
        authTitle: 'Unconstrained Delegation',
        entityTitle: 'Unconstrained Delegation',
        insightsTitle: 'Unconstrained Delegation (Users)',
        description:
            'Unconstrained delegation allows a user or service to impersonate another user (i.e. to use the access permissions of another user, usually more elevated). This practice is very risky and, if abused, could enable an attacker to gain full access to the permissions of the impersonated user.',
        mitigation:
            '1. In Active Directory Users and Computers (ADUC), open the affected computer or service account.\n' +
            '2. Go to Properties > Delegation and clear the option "Trust this computer/user for delegation to any service (Kerberos only)".\n' +
            '3. If delegation is required, configure Constrained Delegation or Resource-Based Constrained Delegation (RBCD) instead.\n' +
            '4. Validate that the application is configurable to constrained delegation and apply the correct configuration.\n' +
            '5. Verify that the service continues to function after the change.',
        reference: {
            desc: 'MITRE Attack TA0004 - Privilege Escalation',
            link: 'https://attack.mitre.org/tactics/TA0004/'
        },
        riskRules: [RISK_RULES.UNCONSTRAINED_DELEGATION_USER],
        customInsightRule: RISK_RULES.UNCONSTRAINED_DELEGATION_USER,
        icons: {
            light: Icons.lightUnconstrainedDelegationIcon,
            dark: Icons.unconstrainedDelegationIcon
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.HIGH,
        type: INDICATOR_TYPES.EXPOSURE
    },
    WEAK_ENCRYPTION: {
        authTitle: 'Weak Encryption Protocol Used',
        entityTitle: 'Weak Encryption Protocol Used',
        insightsTitle: 'Weak Encryption Protocols Used',
        reference: {
            desc: 'MITRE Attack T1110.002 - Brute Force: Password Cracking',
            link: 'https://attack.mitre.org/techniques/T1110/002/'
        },
        riskRules: [RISK_RULES.WEAK_ENCRYPTION],
        icons: {
            light: Icons.lightWeakEncryptionIcon,
            dark: Icons.weakEncryptionIcon
        }
    },
    DOMAIN_CONTROLLER: {
        authTitle: 'Destination is DC',
        entityTitle: 'Domain Controller',
        insightsTitle: 'Domain Controllers',
        riskRules: [RISK_RULES.DOMAIN_CONTROLLER],
        icons: {
            light: Icons.DomainControllerIcon,
            dark: Icons.DomainControllerIcon
        }
    },
    DOMAIN_CONTROLLER_SHADOW: {
        authTitle: 'Rogue Domain Controller',
        entityTitle: 'Rogue Domain Controller',
        insightsTitle: 'Rogue Domain Controller ',
        description:
            'A Rogue Domain Controller attack is a late-stage kill chain attack, designed to change directory objects using malicious replication.\nThis attack can come from any machine, and is performed by creating a rogue domain controller, using a replication process.\nAn attacker registers the computer they are using as a domain controller by making changes to the AD configuration schema and the workstation’s SPN value.\nThey can then replicate changes (for example, adding accounts they control to privileged security groups) to the entire domain.',
        riskRules: [RISK_RULES.DOMAIN_CONTROLLER_SHADOW],
        icons: {
            light: Icons.RiskyDCIcon,
            dark: Icons.RiskyDCIcon
        },
        reason: {
            template:
                '${entityName} is not a DC but has Directory Replication Service (DRS) SPN which could indicate a Rogue Domain Controller (e.g DCShadow) attack.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        adEntityType: AD_ENTITY_TYPES.SERVER,
        customInsightRule: RISK_RULES.DOMAIN_CONTROLLER_SHADOW
    },
    DC_OWNED_BY_NON_ADMIN: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Domain Controller with Non-Admin Owners',
        entityTitle: 'Domain Controller with Non-Admin Owners',
        insightsTitle: 'DC with Non-Admin Owners',
        description:
            'Domain Controller computer accounts owner should be a privileged account.\nIf the DC owner is not a privileged account, the owner might not be highly monitored and protected.\nIn this case, this account may be compromised and the attacker could gain control over the Domain Controller.',
        mitigation:
            '1. In Active Directory Users and Computers (ADUC), enable Advanced Features and open the Domain Controller computer object.\n' +
            '2. Go to Properties > Security > Advanced and change the Owner to a Tier-0 privileged group (e.g., Domain Admins).\n' +
            '3. Verify that the owner account is tightly protected and monitored.\n' +
            '4. Review recent changes to the DC object for any suspicious activity.',
        riskRules: [RISK_RULES.DC_OWNED_BY_NON_ADMIN],
        customInsightRule: RISK_RULES.DC_OWNED_BY_NON_ADMIN,
        adEntityType: AD_ENTITY_TYPES.SERVER,
        icons: {
            light: Icons.RiskyDCIcon,
            dark: Icons.RiskyDCIcon
        },
        extraEntityDetails: [
            {
                moreDetailsFormat: 'guid-list',
                moreDetailsHeader: 'Non-Admin Owners',
                moreDetailsKey: 'unprivileged_owners'
            }
        ],
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.HIGH
    },
    NON_ADMIN_DC_OWNER: {
        authTitle: 'Non-Admin Owner of DCs',
        entityTitle: 'Non-Admin Owner of DCs',
        insightsTitle: 'Non-Admin Owners of DCs',
        description:
            'Domain Controller computer accounts owner should be a privileged account.\nIf the DC owner is not a privileged account, the owner might not be highly monitored and protected.\nIn this case, this account may be compromised and the attacker could gain control over the Domain Controller.',
        mitigation:
            '1. In Active Directory Users and Computers (ADUC), enable Advanced Features and open the Domain Controller computer object.\n' +
            '2. Go to Properties > Security > Advanced and change the Owner to a Tier-0 privileged group (e.g., Domain Admins).\n' +
            '3. Verify that the owner account is tightly protected and monitored.\n' +
            '4. Review recent changes to the DC object for any suspicious activity.',
        riskRules: [RISK_RULES.NON_ADMIN_DC_OWNER],
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            light: Icons.RiskyDCIcon,
            dark: Icons.RiskyDCIcon
        }
    },
    USER_RETROSPECTIVE: {
        authTitle: 'Retrospective email denied by user',
        entityTitle: 'Retrospective email denied by user',
        insightsTitle: 'Retrospective email denied by user',
        riskRules: [RISK_RULES.USER_RETROSPECTIVE],
        icons: {
            light: Icons.UserRetrospectiveIcon,
            dark: Icons.UserRetrospectiveIcon
        },
        customInsightRule: RISK_RULES.USER_RETROSPECTIVE
    },
    TICKET_FROM_NEW_IP: {
        authTitle: 'Ticket from New IP',
        entityTitle: 'Ticket from New IP',
        insightsTitle: 'Ticket from New IP',
        description:
            'Adversaries may "pass the ticket" using stolen Kerberos tickets to move laterally within an environment, without having access to the victim account’s password. A ticket being used from a new IP (for the host) may indicate that the ticket is stolen and being used in such an attack.',
        adEntityType: AD_ENTITY_TYPES.USER,
        riskRules: [RISK_RULES.TICKET_FROM_NEW_IP],
        policyRule: RISK_RULES.TICKET_FROM_NEW_IP,
        reference: {
            desc: 'MITRE Attack T1550.003 - Use Alternate Authentication Material: Pass the Ticket',
            link: 'https://attack.mitre.org/techniques/T1550/003/'
        },
        icons: {
            light: Icons.ticketFromNewIpIcon,
            dark: Icons.ticketFromNewIpIcon
        },
        usedByFilters: true,
        syslogValue: 'Ticket_from_New_IP'
    },
    LOCKED_AFTER_PASSWORD_CHANGE: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Account Locked Post Password Reset',
        entityTitle: 'Account Locked Post Password Reset',
        insightsTitle: 'Account Locked Post Password Reset',
        description: 'The account password was recently rotated and was locked due to an multiple authentication attempts',
        mitigation:
            '1. Update all services, scheduled tasks, and applications that use this account with the new password.\n' +
            '2. Clear cached credentials on dependent systems if applicable.\n' +
            '3. Unlock the account once all dependencies are updated.\n' +
            '4. For future resets, validate dependencies before changing the password.',
        riskRules: [RISK_RULES.LOCKED_AFTER_PASSWORD_CHANGE],
        customInsightRule: RISK_RULES.LOCKED_AFTER_PASSWORD_CHANGE,
        icons: {
            light: Icons.lightLockedAfterChangeIcon,
            dark: Icons.lockedAfterChangeIcon
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.LOW,
        isPostureInsight: true,
        extraEntityDetails: [
            {
                moreDetailsKey: 'pwd_last_set',
                moreDetailsHeader: 'Password last change',
                moreDetailsFormat: 'date'
            },
            {
                moreDetailsKey: 'lockout_time',
                moreDetailsHeader: 'Account locked on',
                moreDetailsFormat: 'date'
            },
        ],
        reason: {
            template: 
                'The password for account ${entityName} was rotated on ${pwd_last_set} and was locked on ${lockout_time}. Note that the account may no longer be locked depending on your security policies.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                pwd_last_set: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE,
                lockout_time: INSIGHTS_FIELD_TYPES.RAW_FIELDS.DATE
            }
        }
    },
    PASSWORD_NOT_REQUIRED: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Password Not Required',
        entityTitle: 'Password Not Required',
        insightsTitle: 'Password Not Required',
        description: 'The account can accept a blank password when changing or resetting a password.',
        mitigation: 'Enforce password requirement with a group policy and audit for accounts violating this policy.',
        reference: {
            desc: 'MITRE Attack T1098 - Account Manipulation',
            link: 'https://attack.mitre.org/techniques/T1098'
        },
        riskRules: [RISK_RULES.PASSWORD_NOT_REQUIRED],
        customInsightRule: RISK_RULES.PASSWORD_NOT_REQUIRED,
        icons: {
            light: Icons.UacFlagIcon,
            dark: Icons.UacFlagIcon
        },
        reason: {
            template:
                'Account ${entityName} can authenticate without a password, according to Password-Not-Required attribute of the account.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        isPostureInsight: true,
        category: RISK_CATEGORIES.UNSECURE_CONFIGURATION,
        severity: RISK_SEVERITY.MEDIUM
    },
    USE_DES_KEY_ONLY: {
        authTitle: 'Kerberos Ticket is Encrypted with Weak Protocol (DES)',
        entityTitle: 'Kerberos Ticket is Encrypted with Weak Protocol (DES)',
        insightsTitle: 'Kerberos Tickets are Encrypted with Weak Protocol (DES)',
        mitigation: 'Ensure that accounts must authenticate with PA data',
        reference: {
            desc: 'MITRE Attack T1558 - Steal or Forge Kerberos Tickets',
            link: 'https://attack.mitre.org/techniques/T1558/'
        },
        riskRules: [RISK_RULES.USE_DES_KEY_ONLY],
        icons: {
            light: Icons.UacFlagIcon,
            dark: Icons.UacFlagIcon
        },
        customInsightRule: RISK_RULES.USE_DES_KEY_ONLY
    },
    DONT_REQUIRE_PREAUTH: {
        type: INDICATOR_TYPES.EXPOSURE,
        authTitle: 'Kerberos Pre-Authentication Not Required',
        entityTitle: 'Kerberos Pre-Authentication Not Required',
        insightsTitle: 'Kerberos Pre-Authentication Not Required',
        reference: {
            desc: 'MITRE Attack T1558 - Steal or Forge Kerberos Tickets',
            link: 'https://attack.mitre.org/techniques/T1558/'
        },
        mitigation: 'Configure accounts to authenticate with pre-authentication data.',
        description:
            'Accounts configured without requiring Kerberos pre-authentication are susceptible to ASREP-roasting attacks.',
        riskRules: [RISK_RULES.DONT_REQUIRE_PREAUTH],
        customInsightRule: RISK_RULES.DONT_REQUIRE_PREAUTH,
        reason: {
            template:
                'Account ${entityName} is configured without the requirement for Kerberos pre-authentication, exposing it to attacks and credential theft.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        icons: {
            light: Icons.UacFlagIcon,
            dark: Icons.UacFlagIcon
        },
        isPostureInsight: true,
        category: RISK_CATEGORIES.CREDENTIAL_ACCESS,
        severity: RISK_SEVERITY.HIGH
    },
    PARTIAL_SECRETS_ACCOUNT: {
        authTitle: 'RODC Account',
        entityTitle: 'RODC Account',
        insightsTitle: 'RODC Accounts',
        reference: {
            desc: 'MITRE Attack TA0004 - Privilege Escalation',
            link: 'https://attack.mitre.org/tactics/TA0004/'
        },
        riskRules: [RISK_RULES.PARTIAL_SECRETS_ACCOUNT],
        icons: {
            light: Icons.UacFlagIcon,
            dark: Icons.UacFlagIcon
        }
    },
    ENCRYPTED_TEXT_PASSWORD_ALLOWED: {
        authTitle: 'User Stores the Password Using Reversible Encryption',
        entityTitle: 'User Stores the Password Using Reversible Encryption',
        insightsTitle: 'Users Store the Password Using Reversible Encryption',
        riskRules: [RISK_RULES.ENCRYPTED_TEXT_PASSWORD_ALLOWED],
        icons: {
            light: Icons.UacFlagIcon,
            dark: Icons.UacFlagIcon
        }
    },
    REPETITIVE_BEHAVIOR: {
        authTitle: 'Repetitive Behavior',
        entityTitle: 'Repetitive Behavior',
        insightsTitle: 'Repetitive Behaviors',
        riskRules: [RISK_RULES.REPETITIVE_BEHAVIOR],
        icons: {
            light: Icons.RepetitiveBehaviorIcon,
            dark: Icons.RepetitiveBehaviorIcon
        }
    },
    ENABLED_BUILT_IN_GUEST: {
        authTitle: 'Enabled Built-in Guest Account',
        entityTitle: 'Enabled Built-in Guest Account',
        insightsTitle: 'Enabled Built-in Guest Accounts',
        mitigation: 'Disable Active-Directory built-in Guest account.',
        riskRules: [RISK_RULES.ENABLED_BUILT_IN_GUEST],
        icons: {
            light: Icons.BuiltInGuestIcon,
            dark: Icons.BuiltInGuestIcon
        }
    },
    RENAMED_BUILT_IN_GUEST: {
        authTitle: 'Renamed Built-in Guest Account',
        entityTitle: 'Renamed Built-in Guest Account',
        insightsTitle: 'Renamed Built-in Guest Accounts',
        mitigation: 'Rename the account to its original value and disable it.',
        riskRules: [RISK_RULES.RENAMED_BUILT_IN_GUEST],
        icons: {
            dark: Icons.userRiskIcon,
            light: Icons.userRiskIcon
        }
    },
    ENABLED_BUILT_IN_ADMIN: {
        authTitle: 'Enabled Built-in Administrator Account',
        entityTitle: 'Enabled Built-in Administrator Account',
        insightsTitle: 'Enabled Built-in Administrator Accounts',
        mitigation: 'Disable the Active Directory built-in admin account.',
        riskRules: [RISK_RULES.ENABLED_BUILT_IN_ADMIN],
        icons: {
            dark: Icons.BuiltInAdminIcon,
            light: Icons.BuiltInAdminIcon
        }
    },
    RENAMED_BUILT_IN_ADMIN: {
        authTitle: 'Renamed Built-in Administrator Account',
        entityTitle: 'Renamed Built-in Administrator Account',
        insightsTitle: 'Renamed Built-in Administrator Accounts',
        mitigation: 'Rename the account to its original value and disable it.',
        riskRules: [RISK_RULES.RENAMED_BUILT_IN_ADMIN],
        icons: {
            dark: Icons.userRiskIcon,
            light: Icons.userRiskIcon
        }
    },
    UNPRIVILEGED_CERTIFICATE_TEMPLATE_MODIFIER: {
        type: INDICATOR_TYPES.EXPOSURE,
        entityTitle: 'ADCS Domain Escalation 4',
        insightsTitle: 'ADCS Domain Escalation 4',
        description:
            'Unprivileged users (non-default principals) with write properties on certificate templates have the ability to escalate their access and create vulnerable certificates to enroll.',
        mitigation:
            'Prevent malicious certificate template modification.\n' +
            'Recommendations:\n' +
            '- Use PSPKIAudit (PowerShell toolkit for auditing Active Directory Certificate Services)to ensure low-privileged user / groups do not have FullControl, WriteOwner, WriteDACL or WriteProperty rights on certificate template objects.\n' +
            '- Monitor for low privileged users with these rights.',
        reference: {
            desc: 'MITRE Attack T1649 - Steal or Forge Authentication Certificates',
            link: 'https://attack.mitre.org/techniques/T1649/'
        },
        icons: {
            dark: Icons.unprivilegedCertificateTemplateModifierIcon,
            light: Icons.unprivilegedCertificateTemplateModifierIcon
        },
        extraEntityDetails: [
            {
                moreDetailsKey: 'certificate_templates',
                moreDetailsHeader: 'Certificate Templates',
                moreDetailsFormat: 'list'
            }
        ],
        syslogValue: 'ADCS_Domain_Escalation_4',
        adEntityType: AD_ENTITY_TYPES.USER,
        riskRules: [RISK_RULES.UNPRIVILEGED_CERTIFICATE_TEMPLATE_MODIFIER],
        customInsightRule: RISK_RULES.UNPRIVILEGED_CERTIFICATE_TEMPLATE_MODIFIER,
        isPostureInsight: true,
        category: RISK_CATEGORIES.PRIVILEGE_ESCALATION,
        severity: RISK_SEVERITY.CRITICAL
    },
    REMOTE_SERVICE_INSTALLATION: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspicious Remote Service Installation',
        entityTitle: 'Suspicious Remote Service Installation',
        insightsTitle: 'Suspicious Remote Service Installation',
        description: `Attackers may attempt to execute remote code by creating or installing a new Windows service on a remote host. This technique is often used for lateral movement, persistence, or privilege escalation within the environment.Suspicious service installation is a strong indicator of compromise, as legitimate administrators rarely install services remotely in day-to-day operations.'`,
        mitigation: `
        1. Restrict remote access to critical servers (e.g., domain controllers) from non-administrative machines.
        2. Review Windows Event ID 7045 and EDR/forensic logs to determine which service was created, the binary executed, and the initiating user.
        3. Remove or disable any unauthorized service immediately, and validate the integrity of the affected system.
        4. Apply the principle of least privilege — only allow specific users the right to create or install services.
        5. Monitor for repeated service creation attempts as potential indicators of automated malware or lateral movement.`,
        reference: {
            desc: 'MITRE ATT&CK T1543.003 – Create or Modify System Process: Windows Service',
            link: 'https://attack.mitre.org/techniques/T1543/003/'
        },
        adEntityType: AD_ENTITY_TYPES.USE,
        icons: {
            dark: Icons.bruteForceIcon,
            light: Icons.bruteForceIcon
        },
        reason: {
            template: 'The user ${entityName} executed remote code on ${server} by remotely installing a service ${serviceName}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                server: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                serviceName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        policyRule: RISK_RULES.REMOTE_SERVICE_INSTALLATION,
        riskRules: [RISK_RULES.REMOTE_SERVICE_INSTALLATION],
        usedByFilters: true,
        syslogValue: 'suspicious_remote_service_installation'
    },
    PRIVILEGED_GROUP_MEMBER_ADDITION: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Suspicious Addition to Privileged Group',
        entityTitle: 'Suspicious Addition to Privileged Group',
        insightsTitle: 'Suspicious Addition to Privileged Group',
        description:
            'Attackers often attempt to escalate privileges or maintain persistence by adding accounts to highly privileged Active Directory groups, such as Domain Admins or Enterprise Admins. ' +
            'This detection identifies when a user account is added to a sensitive group, which may indicate an attempt to gain administrative access or expand control across the environment. ' +
            'Legitimate group membership changes should be infrequent and closely monitored.',
        mitigation:
            '1. Review the event details to determine who performed the group modification and which account was added.\n' +
            '2. Validate whether this change was part of an approved administrative action.\n' +
            '3. If unauthorized, immediately remove the added account from the group and reset its credentials.\n' +
            '4. Audit privileged groups regularly and implement Privileged Access Management (PAM) or Just-In-Time access to limit exposure.\n' +
            '5. Enable and monitor relevant security events on domain controllers (e.g., Event ID 4728).',
        reference: {
            desc: 'MITRE ATT&CK T1098 – Account Manipulation',
            link: 'https://attack.mitre.org/techniques/T1098/'
        },
        adEntityType: AD_ENTITY_TYPES.USER,
        icons: {
            dark: Icons.bruteForceIcon,
            light: Icons.bruteForceIcon
        },
        reason: {
            template: '${entityName} added user ${addedMember} to the privileged group ${groupName} on ${server}.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                addedMember: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                groupName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                server: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        policyRule: RISK_RULES.PRIVILEGED_GROUP_MEMBER_ADDITION,
        riskRules: [RISK_RULES.PRIVILEGED_GROUP_MEMBER_ADDITION],
        usedByFilters: true,
        syslogValue: 'suspicious_privileged_group_member_addition'
    },
    SECURITY_LOG_CLEARED: {
        type: INDICATOR_TYPES.INCIDENT,
        authTitle: 'Security Log Cleared on Domain Controller',
        entityTitle: 'Security Log Cleared on Domain Controller',
        insightsTitle: 'Security Log Cleared on Domain Controller',
        description:
            'The Windows Security Log records authentication events, policy changes, and other security-related activity. ' +
            'Attackers often clear this log to hide traces of malicious operations or privilege escalation steps after compromising a domain controller. ' +
            'Clearing event logs (Event ID 1102) is a strong indicator of anti-forensic behavior and potential system compromise.',
        mitigation:
            '1. Immediately investigate the system where the log was cleared to determine who performed the action and why.\n' +
            '2. Correlate with recent administrative or suspicious activity (e.g., privilege escalation, service creation, or credential dumping).\n' +
            '3. Review backup or forwarded logs (e.g., SIEM or central log collectors) for evidence prior to the clearing event.\n' +
            '4. Implement strict access controls — only authorized administrators should be able to clear logs.\n' +
            '5. Enable Security Log forwarding to ensure redundant storage of all critical events.',
        reference: {
            desc: 'MITRE ATT&CK T1070.001 – Indicator Removal on Host: Clear Windows Event Logs',
            link: 'https://attack.mitre.org/techniques/T1070/001/'
        },
        adEntityType: AD_ENTITY_TYPES.HOST,
        icons: {
            dark: Icons.bruteForceIcon,
            light: Icons.bruteForceIcon
        },
        reason: {
            template:
                'The Security Log on ${server} was cleared by ${entityName}, indicating potential tampering with forensic evidence.',
            fieldsTypes: {
                entityName: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING,
                server: INSIGHTS_FIELD_TYPES.PRIMITIVE_FIELDS.STRING
            }
        },
        policyRule: RISK_RULES.SECURITY_LOG_CLEARED,
        riskRules: [RISK_RULES.SECURITY_LOG_CLEARED],
        usedByFilters: true,
        syslogValue: 'security_log_cleared'
    }
};

// These consts are used by the UI
const USERS_AND_PASS_RULES_LIST = [
    RISK_RULES.DOMAIN_ADMIN,
    RISK_RULES.SHADOW_ADMIN,
    RISK_RULES.AZURE_SHADOW_ADMIN,
    RISK_RULES.ENTRA_ID_SYNCED_ADMIN,
    RISK_RULES.PRIVILEGED_USER,
    RISK_RULES.ADMIN_WITH_SPN,
    RISK_RULES.OLD_PASSWORD,
    RISK_RULES.PASSWORD_NEVER_EXPIRES,
    RISK_RULES.SHARED_USER,
    RISK_RULES.STALE_USER,
    RISK_RULES.LOCKED_USER,
    RISK_RULES.LOCKED_AFTER_PASSWORD_CHANGE,
    RISK_RULES.UNCONSTRAINED_DELEGATION_USER,
    RISK_RULES.AS_REP_ROASTING,
    RISK_RULES.KERBEROASTING,
    RISK_RULES.SMB_ENUMERATION,
    RISK_RULES.BAD_CHECKSUM_USER,
    RISK_RULES.UNPRIVILEGED_DNS_ADMIN
];

const SERVERS_AND_DEVICES_RULES_LIST = [
    RISK_RULES.PRINT_NIGHTMARE_FIX,
    RISK_RULES.SHARED_DEVICE,
    RISK_RULES.OLD_OS,
    RISK_RULES.STALE_RESOURCE,
    RISK_RULES.STALE_DOMAIN_CONTROLLER,
    RISK_RULES.DOMAIN_CONTROLLER_OLD_PASSWORD,
    RISK_RULES.CERTIFRIED,
    RISK_RULES.MIXED_SESSION_HOST,
    RISK_RULES.SERVER_WEAK_ENCRYPTION
];

const THREAT_RISK_RULES_LIST = [
    RISK_RULES.KERBEROASTING,
    RISK_RULES.AS_REP_ROASTING,
    RISK_RULES.SMB_ENUMERATION,
    RISK_RULES.BAD_CHECKSUM_USER,
    RISK_RULES.LOG4SHELL,
    RISK_RULES.PAYLOAD_INJECTION,
    RISK_RULES.USER_BRUTEFORCE_RULE,
    RISK_RULES.HOST_BRUTEFORCE,
    RISK_RULES.SERVER_BRUTEFORCE_RULE,
    RISK_RULES.CREDENTIAL_SCANNING_RULE,
    RISK_RULES.MALICIOUS_IP_RULE,
    RISK_RULES.IMPOSSIBLE_TRAVEL,
    RISK_RULES.NO_PAC,
    RISK_RULES.ACCOUNT_ENUMERATION,
    RISK_RULES.MFA_BOMBING,
    RISK_RULES.CERTIFRIED,
    RISK_RULES.RPC_SEALING_NOT_ENCRYPTED,
    RISK_RULES.DOMAIN_CONTROLLER_SHADOW,
    RISK_RULES.SAPPHIRE_TICKET
];

const ATTACK_RISK_RULES_LIST = [
    RISK_RULES.NTLM_V1,
    RISK_RULES.NTLM_V2,
    RISK_RULES.PASSWORD_NEVER_EXPIRES,
    RISK_RULES.SHADOW_ADMIN,
    RISK_RULES.AZURE_SHADOW_ADMIN,
    RISK_RULES.AZURE_PRIVILEGED_GUEST,
    RISK_RULES.ENTRA_ID_SYNCED_ADMIN,
    RISK_RULES.ADMIN_WITH_SPN,
    RISK_RULES.OLD_PASSWORD,
    RISK_RULES.STALE_USER,
    RISK_RULES.UNCONSTRAINED_DELEGATION_USER,
    RISK_RULES.USER_WEAK_ENCRYPTION,
    RISK_RULES.SERVER_WEAK_ENCRYPTION,
    RISK_RULES.OLD_OS,
    RISK_RULES.FAILED_AUTHS_BATCH,
    RISK_RULES.STALE_RESOURCE,
    RISK_RULES.RODC_PRIVILEGED_CREDENTIALS,
    RISK_RULES.DC_OWNED_BY_NON_ADMIN,
    RISK_RULES.UNPRIVILEGED_CERTIFICATE_TEMPLATE_MODIFIER
];

const getSyslogValueByRiskRule = (riskRule) => {
    const riskIndicator = getRiskIndicatorByRiskRule(riskRule);
    return riskIndicator && riskIndicator.syslogValue ? riskIndicator.syslogValue : null;
};

const createRiskRuleToRiskIndicatorMapping = () => {
    const mapping = {};
    for (const riKey of Object.keys(RISK_INDICATORS)) {
        for (const riskRule of RISK_INDICATORS[riKey].riskRules) {
            mapping[riskRule] = riKey;
        }
    }
    return mapping;
};

const RISK_RULE_TO_RI_KEY_MAPPING = createRiskRuleToRiskIndicatorMapping();

const createRiskIndicatorsForFiltersList = () => {
    const list = [];
    for (const riKey of Object.keys(RISK_INDICATORS)) {
        if (RISK_INDICATORS[riKey].usedByFilters) {
            list.push(riKey);
        }
    }
    return list;
};

const RISK_INDICATORS_FOR_FILTERS_LIST = createRiskIndicatorsForFiltersList();

const createRiskIndicatorsForPolicyList = () => {
    const list = [];
    for (const riKey of Object.keys(RISK_INDICATORS)) {
        if (RISK_INDICATORS[riKey].policyRule) {
            list.push(riKey);
        }
    }
    return list;
};

const RISK_INDICATORS_FOR_POLICY_LIST = createRiskIndicatorsForPolicyList();

const createRiskRulesToSendSplunkList = () => {
    const list = [];
    for (const riskIndicator of Object.values(RISK_INDICATORS)) {
        if (riskIndicator.sendToSplunk) {
            list.push(...riskIndicator.riskRules);
        }
    }
    return list;
};

const RISK_RULES_TO_SEND_SPLUNK_LIST = createRiskRulesToSendSplunkList();

const createRiskRulesToShowOnKRBTGTList = () => {
    const list = [];
    for (const riskIndicator of Object.values(RISK_INDICATORS)) {
        if (riskIndicator.shownForKRBTGT) {
            list.push(...riskIndicator.riskRules);
        }
    }
    return list;
};

const RISK_RULES_TO_SHOW_ON_KRBTGT_LIST = createRiskRulesToShowOnKRBTGTList();

const RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS = Object.entries(RISK_INDICATORS)
    .filter(([, riskIndicator]) => !!riskIndicator.customInsightRule)
    .map(([indicatorKey, riskIndicator]) => {
        return {
            ID: indicatorKey,
            NAME: riskIndicator.insightsTitle,
            ENTITY_TYPE: riskIndicator.adEntityType,
            RISK_RULES: [riskIndicator.customInsightRule],
            ENABLE_INCLUSION: riskIndicator.enableInclusionOnCustomInsight,
            IS_POSTURE_INSIGHT:riskIndicator.isPostureInsight,
        };
    })
    .reduce((map, riskIndicatorObj) => {
        map[riskIndicatorObj.ID] = riskIndicatorObj;
        return map;
    }, {});

const getIconPathByRiskIndicator = (riskIndicatorObj, useLightIcon = false) => {
    const icons = riskIndicatorObj?.icons;
    if (icons?.light || icons?.dark) {
        if ((icons.light && useLightIcon) || !icons.dark) {
            return icons.light;
        }
        return icons.dark;
    }
    return Icons.defaultRiskIcon;
};

const getRiskIndicatorByRiskRule = (riskRule) => {
    const riKey = RISK_RULE_TO_RI_KEY_MAPPING[riskRule];
    return riKey ? RISK_INDICATORS[riKey] : null;
};

const getRiskIndicatorKeyByRiskRule = (riskRule) => {
    return RISK_RULE_TO_RI_KEY_MAPPING[riskRule];
};

const getIconPathByRiskRule = (riskRule, useLightIcon = false) => {
    const riskIndicator = getRiskIndicatorByRiskRule(riskRule);
    if (riskIndicator) {
        return getIconPathByRiskIndicator(riskIndicator, useLightIcon);
    }
    return Icons.defaultRiskIcon;
};

const populateStringTemplate = (stringTemplate, fields, defaultValue) =>
    stringTemplate.replace(INSIGHTS_REASON_FIELDS_REGEX, (match, placeHolder) => {
        const keys = placeHolder.split('|');
        for (const key of keys) {
            if (fields[key] !== undefined && fields[key] !== null && fields[key] !== '') {
                return fields[key];
            }
        }
        return defaultValue ?? match;
    });

const isValidTemplateFields = (stringTemplate, data) => {
    const missingFields = [];
    stringTemplate.replace(INSIGHTS_REASON_FIELDS_REGEX, (_, placeHolder) => {
        const keys = placeHolder.split('|');
        for (const key of keys) {
            if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
                return;
            }
        }

        missingFields.push(...keys);
    });

    return missingFields.length === 0;
};

module.exports = {
    getRiskIndicatorByRiskRule,
    getIconPathByRiskIndicator,
    getIconPathByRiskRule,
    getSyslogValueByRiskRule,
    isValidTemplateFields,
    populateStringTemplate,
    RISK_INDICATORS,
    RISK_RULES,
    RISK_INDICATORS_FOR_FILTERS_LIST,
    RISK_INDICATORS_FOR_POLICY_LIST,
    RISK_RULES_TO_SEND_SPLUNK_LIST,
    RISK_RULES_TO_SHOW_ON_KRBTGT_LIST,
    RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS,
    USERS_AND_PASS_RULES_LIST,
    SERVERS_AND_DEVICES_RULES_LIST,
    THREAT_RISK_RULES_LIST,
    ATTACK_RISK_RULES_LIST,
    getRiskIndicatorKeyByRiskRule,
    RISK_CATEGORIES,
    RISK_SEVERITY,
    INVENTORY_ITEMS
};
